/**
 * Locale strings, ported from `bimgo-ai-web`'s inline `LanguageProvider`
 * dictionary — scoped to what `src/web` actually renders today (the settings
 * feature), not the whole marketing site. Keys are the same dotted strings the
 * web client uses, so the migrated components read `t("settings.…")` unchanged.
 *
 * Grows as more panes are migrated. A missing key falls back to the key itself
 * (see `LocaleProvider`), so an un-migrated string is visible, not blank.
 */

export type Locale = "en" | "zh";

export const LOCALES: readonly Locale[] = ["en", "zh"];

type Dict = Record<string, string>;

const en: Dict = {
  "workflows.beta.enableTitle": "Enable {name}?",
  "workflows.beta.enable": "Enable Beta feature",
  "workflows.beta.warning": "This feature is currently in Beta, may produce errors or unexpected results, and requires a longer time to run. Please carefully assess whether to use it before proceeding.",
  "settings.beta.title": "Beta features",
  "settings.beta.description": "Try features in development. You can turn them off at any time.",
  "settings.beta.empty": "No beta features are available yet.",
  "settings.beta.unavailable": "Connect to the local agent to manage beta features.",
  "settings.beta.saveFailed": "Could not save this setting. Please try again.",
  "skillPatch.count": "{count} changes",
  "skillPatch.preparing": "Preparing changes",
  "skillPatch.ready": "Changes prepared",
  "skillPatch.failed": "Preparation failed",
  "skillPatch.stopped": "Stopped",
  "skillPatch.unconfirmed": "Result unconfirmed",
  "skillPatch.readOnly": "Read-only preview. Preparing changes does not run or save the skill.",
  "skillPatch.diff": "Code changes",
  "skillPatch.operation": "Change {index}",
  "skillPatch.parameters": "Parameter changes",
  "skillPatch.added": "Added",
  "skillPatch.removed": "Removed",
  "skillPatch.modified": "Modified",
  "skillPatch.before": "Before",
  "skillPatch.after": "After",
  "skillPatch.field": "Field",
  "skillPatch.field.name": "Name",
  "skillPatch.field.displayName": "Label",
  "skillPatch.field.type": "Type",
  "skillPatch.field.description": "Description",
  "skillPatch.field.required": "Required",
  "skillPatch.field.default": "Default",
  "skillPatch.field.position": "Position",
  "skillPatch.field.enum": "Options",
  "skillPatch.field.minimum": "Minimum",
  "skillPatch.field.maximum": "Maximum",

  "skillEdit.titlePrepareChanges": "Prepare changes",
  // Work Journal
  "common.showMore": "Show more",
  "common.showLess": "Show less",
  "workJournal.title": "Work Journal",
  "workJournal.empty": "Nothing has been recorded in this conversation yet.",
  "workJournal.failed": "Did not work",
  "workJournal.unconfirmed": "Result not confirmed",
  "workJournal.stepCount": "{count} steps",
  "workJournal.failedCount": "{count} did not work",
  "workJournal.unconfirmedCount": "{count} not confirmed",
  "workJournal.start": "Earlier entries",
  "workJournal.ask": "Ask the agent about this step",
  "workJournal.ref": "Work Journal #{n}",
  "workJournal.question": "Please explain {ref} “{name}”: what it did and how it turned out.",
  "workJournal.viewImage": "View the image",
  "workJournal.viewImages": "View {count} images",
  "workJournal.imageFailed": "This image could not be loaded.",
  "workJournal.continued": "Continued from here",
  // Results
  "endReport.title": "Results",
  "endReport.current": "This round",
  "endReport.resolved": "Resolved this round: {items}",
  "endReport.listJoin": ", ",
  "endReport.view": "View results",
  "endReport.round": "Round {round}",
  "endReport.roundOf": "Round {round} of {total}",
  "endReport.rounds": "Report rounds",
  "endReport.previous": "Previous round",
  "endReport.next": "Next round",
  "endReport.status.done": "Done",
  "endReport.status.partial": "Partly done",
  "endReport.status.not_done": "Not done",
  "endReport.stop.limit": "This round went as far as it could; you can pick up from here.",
  "endReport.stop.error": "Paused after something went wrong; what was finished is kept.",
  "endReport.priority.high": "Needs attention first",
  "endReport.priority.medium": "Worth handling",
  "endReport.priority.low": "Can wait",
  "endReport.results": "Results",
  "endReport.unchecked": "Not checked",
  "endReport.more": "{count} more",
  "endReport.restInList": "{count} more places in the full list",
  "endReport.measureOf": "{done} of {expected} {unit}",
  "endReport.issues": "To do",
  "endReport.count": "{count}",
  "endReport.elementIds": "Revit element IDs",
  "endReport.nextSteps": "Next steps",
  "endReport.chosen": "You chose",
  "endReport.recommended": "Recommended",
  "endReport.continue": "Continue",
  "endReport.continueWith": "Continue with:",
  // Workflow panel
  "workSettings.title": "Workflow",
  "workSettings.refresh": "Refresh",
  "workSettings.initialConditions": "Settings at the start of this conversation",
  "workSettings.submittedInstructions": "Submitted instructions",
  "workSettings.noHistory": "No starting settings were saved for this conversation.",
  "workSettings.unavailable": "This workflow is unavailable. Choose another workflow.",
  "workSettings.incompatible": "This form cannot be opened in your version of BimGo. Update BimGo, then refresh.",
  "workSettings.changed": "You have changed the settings. Apply them again to use them in your message.",
  "workSettings.applied": "Applied to conversation.",
  "workSettings.currentRequest": "Requests and context:",
  "workSettings.submitted": "Submitted",
  "workSettings.readonlyHint": "Starting settings are saved. Continue in the chat to add or change requirements.",
  "workSettings.autoNotice": "This workflow runs automatically: the agent changes the model without asking at each step. You can stop it at any time.",
  "workSettings.execution": "Execution settings",
  "workSettings.estimatedTime": "Estimated time",
  "workSettings.estimateInfo": "A rough reference for a typical drawing, not a completion guarantee or timeout. Actual time varies with drawing complexity, model, and tool-call limit.",
  "workSettings.toolLimit": "Tool call limit",
  "workSettings.toolLimitShort": "Tool limit",
  "workSettings.toolLimitInvalid": "Enter a whole number from {min} to {max}.",
  "workSettings.remove": "Remove workflow",
  "workSettings.existingDraft": "Your message box already contains a draft",
  "workSettings.append": "Append",
  "workSettings.replace": "Replace",
  "workSettings.validation.field": "this field",
  "workSettings.validation.value": "Please check this value.",
  "workSettings.validation.required": "Please fill in this field.",
  "workSettings.validation.maxLength": "Use no more than {limit} characters.",
  "workSettings.validation.minLength": "Use at least {limit} characters.",
  "workSettings.validation.minItems": "Select at least {limit}.",
  "workSettings.checklist.count": "{count} of {total} selected",
  "workSettings.checklist.all": "Select all",
  "workSettings.checklist.none": "Clear all",
  "workSettings.checklist.choose": "Choose",
  "workSettings.validation.option": "Please select a valid option.",
  "workSettings.file.browse": "Browse for a file",
  "workSettings.file.browseFolder": "Choose a folder",
  "workSettings.file.template": "Get a blank form",
  "workSettings.file.generate": "Make the form from the model",
  "workSettings.file.generating": "Reading the model…",
  "workSettings.file.generated": "Form made and selected here: {types} types listed. Fill it in and save it before you start.",
  "workSettings.file.generatedCopy": "A new copy was made and selected here: {types} types listed, with what you had filled in kept. Your earlier file is unchanged.",
  "workSettings.file.generateConnect": "Connect to Revit and open the model first.",
  "workSettings.file.revitBusy": "Revit is busy with a conversation. Wait for it to finish, or stop it, then try again.",
  "workSettings.file.revitFailed": "Revit could not read the model. Check that it is still open with the project loaded, then try again.",
  "workSettings.file.unavailable": "This cannot be done in this run of the app.",
  "workSettings.file.reason.form-not-found": "That file was not found.",
  "workSettings.file.reason.not-a-form": "That file is not a COBie input form. Pick the form, or clear the field to make a new one.",
  "workSettings.file.reason.no-types": "The model has no components in the selected categories, so there are no types to list.",
  "workSettings.file.reason.file-busy": "The form could not be written. If it is open in Excel, close it and try again.",
  "workSettings.file.templateSaved": "Blank form saved and selected here. Fill it in and save it before you start.",
  "workSettings.file.templateBusy": "Could not save there. If a file of that name is open, close it and try again.",
  "workSettings.file.failed": "Could not complete that. Try again, or type the path.",
  "workSettings.context.indexUnavailable": "Refresh to update model data.",
  "workflows.noRevit": "Open Revit to use this workflow",
  "workSettings.context.blocked": "Update model data",
  "workSettings.context.refreshFailed": "Could not refresh model data. Check Revit and try again.",
  "workSettings.context.modelChanged": "Model data has changed. Reload these options.",
  "workSettings.context.reselect": "Model data has changed. Select the item again.",
  "workSettings.context.noItems": "No items available.",
  "workSettings.context.select": "Select an item",
  "workSettings.context.agentChoice": "Decide automatically",
  "workSettings.context.connect": "Connect and select Revit first.",
  "workSettings.context.hostChanged": "Your Revit connection or selection has changed. Check the selected Revit, then refresh and select the item again.",
  "workSettings.context.modelChangedReselect": "Model data has changed. Refresh and select the item again.",
  "workSettings.context.typeMismatch": "This item cannot be used in this field. Select another item.",
  "workSettings.context.itemUnavailable": "The previously selected item could not be found. Select another item.",
  // Settings shell
  "settings.title": "Settings",
  "settings.subtitle": "Your account and this build.",
  "settings.section.user": "User",
  "settings.section.organization": "Organization",
  "settings.section.wallet": "Billing & Plan",
  "account.organizations": "Organizations",
  "account.signOut": "Sign out",

  // Desktop titlebar menu
  "menu.label": "Menu",
  "menu.view": "View",
  "menu.reload": "Reload",
  "menu.devtools": "Developer Tools",
  "menu.help": "Help",
  "menu.about": "About",
  "menu.packageLogs": "Package logs",
  // Report an issue (bottom of the sidebar)
  "ticket.open": "Report an issue",
  "ticket.title": "Report an issue",
  "ticket.subtitle": "Tell us what went wrong. A screenshot helps us find the cause faster.",
  "ticket.titleLabel": "Title",
  "ticket.titlePlaceholder": "Short summary of the problem",
  "ticket.descriptionLabel": "Description",
  "ticket.descriptionPlaceholder": "What happened? What were you doing, and what did you expect?",
  "ticket.screenshotLabel": "Screenshot (optional)",
  "ticket.screenshotHint": "One png, jpg, gif or webp image, up to {limit} MB.",
  "ticket.chooseScreenshot": "Choose screenshot",
  "ticket.removeScreenshot": "Remove",
  "ticket.fileError.unsupported_type": "{file} is not supported. Only screenshots (png, jpg, gif, webp) can be attached.",
  "ticket.fileError.exceeds_size": "{file} is larger than the {limit} MB limit.",
  "ticket.fileError.empty": "{file} is empty.",
  "ticket.submit": "Submit",
  "ticket.submitting": "Submitting…",
  "ticket.submitted": "Issue reported — reference #{number}. Thanks for letting us know!",
  "ticket.submitFailed": "Could not submit your report: {message}",
  // The caption buttons (bridge v8), drawn by the titlebar itself.
  "window.minimize": "Minimize",
  "window.maximize": "Maximize",
  "window.restore": "Restore down",
  "window.close": "Close",

  // About — the technical detail the status bar deliberately no longer carries.
  "hosts.connectedTitle": "Connected instances",
  "connection.agent.ready": "Agent connected",
  "connection.agent.resolving": "Checking Agent connection…",
  "connection.agent.starting": "Starting Agent…",
  "connection.agent.no-desktop": "Agent unavailable in this browser",
  "connection.agent.no-agent-channel": "Agent connection unavailable in this desktop version",
  "connection.agent.stopped": "Agent is not running",
  "connection.agent.failed": "Could not connect to Agent",
  "connection.agent.failedDetail": "Could not connect to Agent: {detail}",
  "connection.hosts.unsupported": "Revit / AutoCAD detection unavailable",
  "connection.hosts.loading": "Checking Revit / AutoCAD connections…",
  "connection.hosts.error": "Could not check Revit / AutoCAD connections",
  "connection.hosts.none": "No Revit / AutoCAD connections",

  // The chat header's CAD instance binding — one thread, one instance.
  "hosts.none": "no connected instances",
  "instance.lockedDuringTurn": "Running — switching is available when execution finishes",
  "instance.totalCount": "{count} total",
  "instance.connectedCount": "{count} connected",
  "instance.current": "Current",
  "instance.switched": "Switched to {product} · {name}",
  "instance.autoSwitched": "The previous connection closed. Switched to {product} · {name}",
  "about.title": "About Bimgo AI 2.0",
  "about.desktop": "Desktop",
  "about.plugin": "Installed plugin",
  "about.instances": "Connected instances",
  "about.loading": "checking…",
  // "could not look" — never the same sentence as "looked and found none".
  "about.unavailable": "not reported by this desktop build",
  "about.pluginNone": "none installed",
  "about.instancesNone": "none connected",
  "about.untitled": "untitled",
  "common.close": "Close",

  // Chat history — wording from `bimgo-ai-web`'s chat.* / common.history keys.
  // Dashboard — wording from `bimgo-ai-web`'s dashboard.* keys.
  "dashboard.title": "Usage Dashboard",
  "dashboard.subtitle": "Review your monthly usage and recent request trend.",
  "dashboard.section.personal": "Personal Usage",
  "dashboard.section.organization": "Organization Usage",
  "dashboard.admin.members.colRemainingTokens": "Tokens Remaining",
  "dashboard.admin.members.sortLabel": "Sort members",
  "dashboard.admin.members.sort.requests": "Sort by requests",
  "dashboard.admin.members.sort.tokens": "Sort by tokens",
  "dashboard.admin.members.sort.remainingTokens": "Sort by tokens remaining",
  "dashboard.admin.members.sort.email": "Sort by email",

  // Plugins — vendor CAD integrations. The local agent is not one of them; it
  // ships inside the app and is reported in the shell footer instead.
  "plugins.title": "Plugins",
  "plugins.subtitle": "A first install or a major update requires closing Revit or AutoCAD before installing.",
  "pluginManage.title": "Manage the {host} plugin",
  "pluginManage.refresh": "Detect again",
  "pluginManage.detecting": "Detecting…",
  "pluginManage.empty.current": "Every detected {host} version is up to date.",
  "pluginManage.empty.unavailable": "An installable version could not be found for some detected years. The update source may be unreachable. Check your connection and try detecting again.",
  "pluginManage.empty.no-hosts": "No {host} installation was detected on this machine.",
  "pluginManage.empty.no-candidates": "Nothing to install or update. Unsupported years and developer builds are excluded.",
  "pluginManage.kind.install": "Install",
  "pluginManage.kind.update": "Update",
  "pluginManage.kind.downgrade": "Roll back",
  "pluginManage.kind.repair": "Repair",
  "pluginManage.pending": "Manage: {count} waiting to install or update",
  "pluginManage.found": "{count} version(s) can be installed or updated.",
  "pluginManage.selectAll": "Select all",
  "pluginManage.selected": "{count} of {total} selected",
  "pluginManage.progress.write": "Installing… {done} of {total} finished",
  "pluginManage.progress.remove": "Removing… {done} of {total} finished",
  "pluginManage.summary.done": "{count} version(s) done.",
  "pluginManage.summary.partial": "{done} done, {failed} failed.",
  "pluginManage.closeFirst": "{host} {year} is open. Close it before running.",
  "pluginManage.run.confirm": "Confirm ({count})",
  "pluginManage.run.remove": "Remove ({count})",
  "pluginManage.removeConfirm.title": "Remove the {host} plugin?",
  "pluginManage.removeConfirm.body": "The plugin will be removed from these {host} versions. You can install it again from Manage at any time.",
  "pluginManage.result.queued": "Waiting…",
  "pluginManage.result.running": "Working…",
  // On Confirm while the run checks that no affected host is open, before anything is written.
  "pluginManage.preparing": "Preparing…",
  "pluginManage.result.done": "Done",
  "pluginManage.result.failed": "Failed: {reason}",
  "pluginManage.restart": "Start {host} again to load the new plugin.",
  "plugins.cancel": "Cancel",
  "plugins.close": "Close",
  "plugins.retry": "Try again",
  "plugins.manage.title": "Manage {host} plugin",
  "plugins.manage.body": "Install, repair, update, or remove the plugin for each detected {host} version.",
  "plugins.toast.dismiss": "Dismiss",
  "plugins.checking": "Checking installed plugins…",
  "plugins.unsupported": "This desktop version cannot manage plugins.",
  "plugins.unsupportedHint":
    "Update the desktop app to install and check vendor software plugins from here.",
  "plugins.detectFailed": "Could not check installed plugins.",
  "plugins.status.updateAvailable": "Available for upgrades",
  "plugins.status.rollbackAvailable": "Roll back available",
  "plugins.state.updateTo": "update to {version}",
  "plugins.state.installTo": "install {version}",
  "plugins.state.downgradeTo": "roll back to {version}",
  "plugins.install.target": "Version {version} will be installed.",
  "plugins.install.fromNetwork": "Downloading the latest published build.",
  "plugins.install.fromApp": "Using the build included with this app.",
  "plugins.status.running": "Running",
  "plugins.status.installed": "Installed",
  "plugins.status.attention": "Needs attention",
  "plugins.status.notInstalled": "Not installed",
  "plugins.status.sessions": "{count} open",
  "plugins.state.ready": "Installed",
  "plugins.state.external": "Loaded from a local build",
  "plugins.state.incomplete": "Installed, not registered with Revit",
  "plugins.state.orphaned": "Registered but missing files",
  "plugins.state.absent": "Not installed",
  "plugins.state.unsupported": "Not supported yet",
  "plugins.state.openNow": "open now",
  "plugins.state.restartRequired": "Restart {host} required for the pinned host",
  "plugins.card.manage": "Manage",
  "plugins.card.remove": "Remove",
  "plugins.card.update": "Update",
  "plugins.card.updating": "Updating…",
  "plugins.card.install": "Install",
  "plugins.card.installing": "Installing…",
  "plugins.card.installHint": "Downloads and installs — close this {host} version first.",
  "plugins.card.installedLabel": "Installed for {host}",
  "plugins.card.repair": "Repair",
  "plugins.card.repairHint": "Reinstalls the pinned host and command set — close this {host} version first.",
  "plugins.card.downgrade": "Downgrade",
  "plugins.card.downgradeHint": "Rolls back to an earlier build — close this {host} version first.",
  "plugins.progress.queued": "Queued…",
  "plugins.progress.working": "Working…",
  "plugins.progress.downloading": "Downloading…",
  "plugins.progress.installing": "Installing…",
  "plugins.progress.removing": "Removing…",
  "plugins.progress.checking": "Checking…",
  "plugins.progress.waitingClose": "Waiting…",
  // Asked before a whole-tree write while that year's host is open (#189).
  "plugins.closeHost.title": "Close {host} {years} first",
  "plugins.closeHost.body": "Installing replaces the plugin files {host} {years} has loaded, so it has to be closed. Save your work, close every {host} {years} window, then choose Continue.",
  "plugins.closeHost.stillOpen": "{host} {years} is still open. Close it, then choose Continue again.",
  "plugins.closeHost.continue": "Continue",
  "plugins.closeHost.checking": "Checking…",
  "plugins.update.hostOpen": "{host} {years} is open, so nothing was installed. Close it and try again.",
  "plugins.card.updateHotHint": "Applies live — Revit can stay open.",
  "plugins.card.closeFirst": "Close this {host} session first.",
  "plugins.card.restartRequired": "Updates the pinned host — restart {host} to finish.",
  "plugins.remove.done": "Removed.",
  "plugins.remove.failed": "Could not remove the plugin.",
  "plugins.update.done": "Updated to {version}. Restart {host} to load the pinned host.",
  "plugins.update.failed": "Could not update the plugin.",
  "plugins.update.installed": "Installed {version}. Restart {host} to load the pinned host.",
  "plugins.update.installFailed": "Could not install the plugin.",
  "plugins.update.downgraded": "Rolled back to {version}. Restart {host} to load the pinned host.",
  "plugins.update.downgradeFailed": "Could not roll back the plugin.",
  "plugins.update.repaired": "Repaired {version}. Restart {host} to load the pinned host.",
  "plugins.update.repairFailed": "Could not repair the plugin.",
  "plugins.update.reloaded": "Command set reloaded in {count} open session(s).",
  "plugins.update.reloadIdle": "Updated. No restart needed.",
  "plugins.update.reloadPartial": "Command set reloaded in {reloaded}/{requested} session(s). Restart {host} to finish.",
  "plugins.update.reloadFailed": "Command set updated on disk. Restart {host} to finish.",
  "plugins.session.connected": "Connected",
  "plugins.session.notResponding": "Not responding",
  "plugins.session.checking": "Checking…",
  "plugins.host.untitled": "Untitled document",
  // The Plugins page: every catalog entry is a card, with ＋ before the first install.
  "plugins.catalog.comingSoon": "Launching soon",
  "plugins.catalog.add": "Add {name}",
  "plugins.catalog.revit.description":
    "Work with your Revit model in chat — read elements, edit parameters and run commands without leaving the conversation.",
  "plugins.catalog.autocad.description":
    "Work with AutoCAD drawings directly in chat: read layers, edit linework, and run commands.",
  "plugins.install.modalTitle": "Install for Revit",
  "plugins.install.modalBody": "Choose the Revit versions to install the plugin for.",
  "plugins.install.action": "Install",
  "plugins.install.patchAction": "Update now",
  "plugins.install.installing": "Installing…",
  "plugins.install.done": "Installed. Restart Revit to load the plugin.",
  "plugins.install.patchHint": "This is a small update — it applies while Revit stays open.",
  "plugins.install.patchDone": "Updated. Any open Revit picked it up — no restart needed.",
  "plugins.install.noBuild": "No plugin version could be found right now; the update source may be unreachable. What is installed keeps working.",
  "plugins.install.noHost": "No {host} installation was found on this machine.",
  "plugins.install.unsupportedHost":
    "Found {host} {found}, but only {supported} are supported.",
  "plugins.install.availableYears": "Plugin builds are currently published for {host} {years}.",

  "dashboard.summary.title": "Current Cycle Usage",
  "dashboard.summary.plan": "Plan",
  "dashboard.summary.requests": "requests",
  "dashboard.summary.tokens": "tokens",
  "dashboard.summary.remaining": "Remaining",
  "dashboard.summary.unlimited": "Unlimited",
  // The member's own wallet ceiling, shown as spent / limit. Named for what it
  // is rather than "limit", which three different things were called before
  // `bimgo-ai-web` #181; wording verbatim from there.
  "dashboard.summary.creditLimit": "Organization AI Token Personal Usage",
  "dashboard.summary.resetsAt": "Resets on",
  "dashboard.summary.noPlan": "No active plan found.",
  "dashboard.summary.loadFailed": "Failed to load usage status. Please try again later.",
  // The trend windows, and the organisation analytics — wording verbatim from
  // `bimgo-ai-web`. `rawTokens` keeps its key: the field it labelled was renamed
  // to `credits`, the label was not, and renaming the key here would put the two
  // clients' dictionaries out of step for no visible gain.
  "dashboard.chart.range.7d": "Last 7 days",
  "dashboard.chart.range.14d": "Last 14 days",
  "dashboard.chart.range.30d": "Last 30 days",
  "dashboard.chart.range.90d": "Last 90 days",
  "dashboard.analytics.currentCycle": "Current billing cycle",
  "dashboard.analytics.historical": "Historical usage analytics",
  "dashboard.analytics.customRange": "Custom date range",
  "dashboard.analytics.apply": "Apply",
  "dashboard.analytics.reset": "Reset",
  "dashboard.analytics.requests": "Requests",
  "dashboard.analytics.rawTokens": "Token",
  "dashboard.analytics.activeMembers": "Active members",
  "dashboard.analytics.activeUsers": "Active users",
  "dashboard.analytics.cloudSkillsTotal": "Cloud Skills",
  "dashboard.analytics.cloudSkillsCreated": "New Cloud Skills",
  "dashboard.analytics.trends": "Usage trends",
  "dashboard.analytics.date": "Date",
  "dashboard.analytics.empty": "No usage activity in this range.",
  "dashboard.analytics.loadFailed":
    "Failed to load organization analytics. Please try again later.",
  "dashboard.analytics.rangeIncomplete": "Select both a start and end date.",
  "dashboard.analytics.rangeTooLong": "The date range cannot exceed 366 days.",
  // The two date boxes have no counterpart in the reference, which opens a
  // calendar rather than asking for a start and an end.
  "dashboard.analytics.from": "From",
  "dashboard.analytics.to": "To",
  "dashboard.chart.title": "Usage Trend",
  "dashboard.chart.requests": "Requests",
  "dashboard.chart.tokens": "Tokens",
  "dashboard.chart.empty": "No usage recorded in this period.",
  "dashboard.chart.loadFailed": "Failed to load trend data. Please try again later.",
  "dashboard.admin.summary.title": "Organization Cycle Usage",
  "dashboard.admin.summary.plan": "Organization Plan",
  "dashboard.admin.summary.noPlan": "No active plan",
  "dashboard.admin.summary.resetsAt": "Resets on",
  "dashboard.admin.summary.requests": "Requests this cycle",
  "dashboard.admin.summary.tokens": "Tokens this cycle",
  "dashboard.admin.summary.activeMembers": "Active members",
  "dashboard.admin.summary.totalMembers": "Total members",
  "dashboard.admin.summary.noData": "No organization usage data yet.",
  "dashboard.admin.summary.loadFailed":
    "Failed to load organization usage. Please try again later.",
  "dashboard.admin.chart.title": "Organization Request Trend",
  "dashboard.admin.members.title": "Members Usage",
  "dashboard.admin.members.colEmail": "Email",
  "dashboard.admin.members.colRequests": "Requests",
  "dashboard.admin.members.colTokens": "Tokens",
  "dashboard.admin.members.empty": "No members in this organization yet.",

  // The organization gate — `bimgo-ai-web`'s two blocks at the top of
  // `pages/Projects.tsx`. Verbatim except for `subscriptionExpiredDesc`, which
  // names the **application** where v2 named projects — v2 gated one page and
  // could say so, this gates the product — and drops v2's leading "your
  // organization has no valid subscription" clause, which the title one line
  // above already says (owner).
  // `subscriptionExpiredContactBimgo` is v2's `subscriptionExpiredContactAdmin`
  // renamed for what it actually says; that text is unchanged.
  // `backToLogin` and `...SwitchOrg` have no v2 counterpart.
  "projects.noOrganization": "No Organization",
  "projects.noOrganizationDesc": "You are not a member of any organization",
  "projects.contactAdmin": "Please contact your administrator to join an organization",
  "projects.subscriptionExpired": "Subscription Expired",
  "projects.subscriptionExpiredDesc": "Bimgo AI 2.0 is unavailable.",
  "projects.subscriptionExpiredContactBimgo":
    "Please contact Bimgo to renew or activate the organization subscription",
  "projects.subscriptionExpiredSwitchOrg":
    "You can switch to another organization from the account menu.",
  "projects.backToLogin": "Back to login",


  // Billing & Plan (#64) — the wallet, ported from `bimgo-ai-web`'s wallet.* /
  // limits.* keys. Everything is in BIMGO credits; there is no money concept on
  // the client, which is why nothing here names a currency.
  "wallet.loadFailed": "Failed to load data.",
  "wallet.subscription.title": "Subscription",
  "wallet.subscription.none": "No active subscription.",
  "wallet.subscription.active": "Active",
  "wallet.subscription.plan": "Plan name",
  "wallet.subscription.renewsOn": "Renews on",
  "wallet.subscription.period": "Billing period",
  "wallet.subscription.daysLeft": "{count} days left",
  "wallet.plan.title": "Plan",
  "wallet.plan.empty": "No plan attached.",
  "wallet.plan.maxCredits": "Monthly Allowance AI Token limit",
  "wallet.plan.cycleReset": "Monthly reset on",
  "wallet.balance.title": "Bimgo AI Token Balance",
  "wallet.balance.expiringSoon": "{credits} AI Token are expiring soon.",
  "wallet.balance.nextExpiry": "Next expiry: {date}.",
  "wallet.purchases.title": "Purchases & top-ups",
  "wallet.purchases.colDate": "Date",
  "wallet.purchases.colType": "Type",
  "wallet.purchases.colCredits": "Credits",
  "wallet.purchases.colBalance": "Balance after",
  "wallet.purchases.colExpires": "Expires",
  "wallet.purchases.colReference": "Reference",
  "wallet.purchases.empty": "No purchases or top-ups yet.",

  // Spend limits. `null` is "no cap / inherit" and `0` is "may spend nothing" —
  // which is why the two hints below say what a *blank* box means, separately
  // per plan mode, rather than calling it zero.
  "limits.save": "Save",
  "limits.saving": "Saving…",
  "limits.cancel": "Cancel",
  "limits.edit": "Edit",
  "limits.unlimited": "Unlimited",
  "limits.invalid": "Enter a number of 0 or more, or leave blank.",
  "limits.saveFailed": "Could not save. Please try again.",
  "limits.plan.mode": "Plan mode",
  "limits.plan.seats": "Seats",
  "limits.mode.per_seat": "Per seat",
  "limits.mode.org_shared": "Enterprise",
  "limits.org.title": "Monthly organization AI Token limit",
  "limits.org.editTitle": "Set monthly organization AI Token limit",
  "limits.org.hint":
    "Caps the organization tokens the whole team can use each month. Prepaid seat allowances do not count toward it.",
  "limits.org.default": "Default",
  "limits.org.placeholder": "e.g. 75000000",
  "limits.org.clearHint": "Leave blank to restore the default (seats × plan allowance).",
  "limits.members.title": "Members Organization AI Token Usage",
  "limits.members.editTitle": "Set member monthly Bimgo AI Token limits",
  "limits.members.hint": "The most each member may draw from the organization wallet per month.",
  "limits.members.hintPerSeat":
    "Per seat — the personal allowance is spent first, and only what goes beyond it draws on the wallet.",
  "limits.members.hintOrgShared":
    "Enterprise — there is no personal allowance, so every request draws on the wallet.",
  "limits.members.blankPerSeat":
    "Blank means 0 — nothing is drawn from the wallet until you set a limit.",
  "limits.members.blankOrgShared":
    "Blank means no per-member limit — only the organization limit and wallet balance apply.",
  "limits.members.colMember": "Member",
  "limits.members.colCap": "Personal Limit",
  "limits.members.colSpent": "Personal Spent",
  "limits.members.exhausted": "Limit reached",
  "limits.members.inherit": "Blank: use default",
  "limits.members.empty": "This organization has no members yet.",
  "limits.members.applyAll": "Apply to all members:",
  "limits.members.applyAllPlaceholder": "e.g. 15000000",
  "limits.members.applyAllAction": "Apply to all",

  // Failure surfaces — the /ask error catalogue, verbatim from `bimgo-ai-web`.
  "error.validationError": "Invalid request. Please check your input and try again.",
  "error.unauthorized": "Your session has expired. Please log in again.",
  "error.forbidden": "You do not have permission to perform this action.",
  "error.notFound": "The requested resource was not found. Please try again.",
  "error.instanceBusy": "{instance} is busy with another chat. Wait for that chat to finish, or stop it, then try again.",
  "error.instanceBusyUnknown": "The selected instance is busy with another chat. Wait for that chat to finish, or stop it, then try again.",
  "error.threadBusy": "This conversation is still answering. Wait for it to finish, or stop it, then send again.",
  "threadStatus.unavailable": "This conversation isn't available for this account or server.",
  "threadStatus.ownerMismatch": "This conversation belongs to another workspace, so it can't continue here.",
  "threadStatus.noLocalWorkspace": "This conversation has no workspace on this device, so it can't continue here. You can still read it.",
  "threadStatus.loadFailed": "The conversation could not be loaded.",
  "threadStatus.startNew": "Start new",
  // A cut-off workflow run asked to continue somewhere other than the model it was working in.
  "error.runTargetChanged": "This run was working in \"{document}\". Open that model in {host} and select it, then continue the run again. To carry on in the model selected now, send a new message instead.",
  "error.runTargetChangedUnknown": "This run was working in a different model. Open that model in {host} and select it, then continue the run again. To carry on in the model selected now, send a new message instead.",
  "error.runTargetUnknown": "This run cannot be continued because there is no record of the model it was working in. Send a new message to carry on.",
  // One 402, four blocks (#64). The generic sentence is the fallback for a
  // refusal that names no reason; it no longer says "wait until next month",
  // which was wrong for three of the four and never true for a lapsed
  // subscription. See `askError.errorKeyFor`.
  "error.usageLimitExceeded":
    "This request was blocked by your organization's billing limits. See Settings › Billing & Plan, or contact your organization administrator.",
  "error.subscriptionRequired":
    "Your organization does not have an active subscription. Please contact Bimgo to renew or activate it.",
  "error.insufficientCredits":
    "Your organization has run out of Bimgo AI Tokens. An organization administrator can top up the balance.",
  "error.memberLimitExceeded":
    "You have used your monthly allowance and have no pay-as-you-go budget in this organization. An organization administrator can grant or raise it.",
  "error.orgLimitExceeded":
    "Your organization has reached its monthly spending limit. An organization administrator can raise it.",
  "error.contextLengthExceeded":
    "The conversation is too long. Please start a new conversation and try again.",
  "error.pluginUpdateRequired":
    "Update the Revit plugin from Plugins, restart Revit, then try again.",
  "error.agentRecursionLimit":
    "The AI agent exceeded its maximum processing steps. Please simplify your request or try again.",
  "error.agentToolError": "An AI tool failed to execute. Please try again.",
  "error.agentParseError": "Failed to parse AI response. Please try again.",
  "error.modelAuthenticationError":
    "AI model authentication failed. Please contact your administrator.",
  "error.modelNotFound": "The specified AI model was not found. Please contact your administrator.",
  "error.modelRateLimit": "Too many AI requests. Please wait a moment and try again.",
  // Distinct from the rate limit on purpose (#129): that one is our request rate and
  // clears by slowing down, this one is the provider being saturated and clears on
  // its own. Saying "try again" without "unexpected error" is the whole point — the
  // user's app is not broken.
  //
  // Named where the agent told us which provider failed, because "OpenAI is busy" is
  // something a user can reason about — they may know it is having a bad day, and it
  // explains why switching models might help. The unnamed variant is the fallback for
  // an error that arrived without the detail, never a `{provider}` left on screen.
  "error.modelOverloaded": "{provider} is busy right now. Please try again in a moment.",
  "error.modelOverloadedUnknown": "The AI provider is busy right now. Please try again in a moment.",
  "error.modelConnectionLost": "{provider}: the connection dropped before the answer finished. Please try again.",
  "error.modelConnectionLostUnknown": "The connection to the AI provider dropped before the answer finished. Please try again.",
  "error.modelAborted": "AI processing was interrupted. Please try again.",
  "error.upstreamError": "The service is temporarily unavailable. Please try again later.",
  "error.serviceUnavailable": "The service is currently unavailable. Please try again later.",
  "error.internalError": "An unexpected error occurred. Please try again later.",
  "error.unknown": "An unexpected error occurred. Please try again later.",

  // Plan mode — wording from `bimgo-ai-web`'s hitl.* / activePlan.* keys.
  "hitl.approvalRequired": "Approval required",
  "hitl.approved": "Approved",
  "hitl.rejected": "Rejected",
  "hitl.revised": "Changes requested",
  "hitl.answered": "Answered",
  "hitl.yourAnswer": "Your answer: ",
  "hitl.askUserTitle": "Needs your input",
  "hitl.askUserOtherPlaceholder": "Other (type your own answer)",
  "hitl.askUserSubmit": "Send answer",
  "hitl.askUserCancel": "Cancel",
  "hitl.askUserCancelHint": "Stop the current task; previously completed changes are kept",
  "hitl.generateCode": "Generate code",
  "hitl.executeSkill": "Execute skill",
  "hitl.saveSkill": "Save skill",
  "hitl.planReview": "Review plan",
  "hitl.planLabel": "Plan",
  "hitl.editPlan": "Edit plan",
  "hitl.planFeedbackPlaceholder": "How should the plan change? Edit it above or comment here",
  "hitl.requestChanges": "Request changes",
  "hitl.execute": "Execute",
  "hitl.approve": "Approve",
  "hitl.reject": "Reject",
  "hitl.approveBlockedByComment":
    "You have written a comment. Use \"Request changes\", or clear it to approve.",
  "activePlan.label": "Plan in progress",
  "activePlan.dismiss": "Dismiss plan",
  "activePlan.close": "Close",
  "activePlan.updated": "Plan updated — click to view",
  "activePlan.download": "Download plan (.md)",
  "activePlan.copy": "Copy plan",
  "activePlan.copied": "Copied",

  // Starter prompts — wording from `bimgo-ai-web`'s starterPrompts.* keys.
  "starterPrompts.sectionLabel": "Project starter prompts",
  "starterPrompts.cta": "Start Co-Work With Bimgo AI",
  "starterPrompts.ctaPrefix": "Start Co-Work With",
  "starterPrompts.ctaSuffix": "",
  /**
   * Two different situations, two sentences — and the old one served both badly.
   *
   * "The current model is unavailable" was read as the *LLM* being down, because
   * "model" means the language model everywhere else in this app. It actually meant
   * "no BIM model is open", which is the user's to fix and was never said. So the
   * actionable case now names Revit and the action; the rest stays a plain statement,
   * because nothing the user can do will help.
   */
  // The skills list's pager (#123 §1). Client-side only — it pages what is already
  // loaded, so `total` is the whole set, not a server count.
  // The heading's vendor control. "CAD service" was the house term and it is the accurate
  // one — the pane is per vendor and AutoCAD is listed behind it — but beta ships Revit
  // alone (owner), and a category nobody can pick a second member of reads as jargon. The
  // plainer word says the same thing and needs no glossary.
  "skills.vendorLabel": "Application",
  // Shown on a vendor the desktop does not serve yet. The option is disabled, so it says
  // where the product is going without offering a scope with no store behind it.
  "skills.vendorComingSoon": "coming soon",
  // The read-only year pill on the title row. The name has to carry the whole story,
  // because the control shows only a number and cannot be operated: the year is real, it
  // is a choice, and the choice is made in the status bar. Phrased as the **version**
  // rather than the instance (owner) — that is what the pill reads and what the store is
  // keyed by; "instance" is our word for a process, not the user's.
  "skills.versionScopeLabel": "Showing skills for the Revit version selected below",
  // The hint beside a year in that pill's list — the ones with an instance open.
  "skills.versionOpen": "open",
  // The sort control names a direction, not a key: the version is always the primary
  // key, ascending, and this orders the skills within each version.
  "skills.sortLabel": "Sort by",
  "skills.sort.newest": "Newest first",
  "skills.sort.oldest": "Oldest first",
  "pager.label": "List pages",
  "pager.prev": "Previous page",
  "pager.next": "Next page",
  /**
   * One line under the list: the page and the total in **one sentence** (owner) — the two
   * were separate elements at opposite ends of the footer and read as unrelated numbers.
   *
   * Four keys rather than a noun interpolated into one, because a counted noun is not a slot
   * in every language. The `total` forms are what a single page shows, where the page half
   * has nothing to say.
   */
  "pager.pagesSkills": "Page {page} of {pageCount} - total {total} skills",
  "pager.pagesItems": "Page {page} of {pageCount} - total {total} items",
  "pager.totalSkills": "total {total} skills",
  "pager.totalItems": "total {total} items",
  "skills.pageSize": "Cards per page",
  "skills.pageSizeOption": "{count} / page",
  "starterPrompts.openModelFallback":
    "Open a BIM model in Revit to get starter prompts written about your own project. These are general examples.",
  "starterPrompts.genericFallback":
    "Could not read the open BIM model, so these are general examples.",
  "starterPrompts.loading": "Understanding the open BIM model…",
  // "the current model context" had the same ambiguity, on a path where the BIM model
  // *is* open — so these say which model they mean.
  "starterPrompts.usageFallback": "AI credits are unavailable, so these examples come from the open BIM model without AI.",
  "starterPrompts.contextFallback": "AI generation is unavailable, so these examples come from the open BIM model without AI.",
  "settings.user.timeout.title": "Revit Task Timeout",
  "settings.user.timeout.description": "How long the AI waits for a single Revit task to finish. Raise it for heavy work such as bulk modelling.",
  "settings.user.timeout.seconds": "{seconds} seconds",
  "settings.user.timeout.default": "default",
  "settings.user.timeout.loading": "Loading…",
  "settings.user.timeout.unavailable": "Cannot reach the local agent, so this cannot be changed right now.",
  "settings.user.timeout.savedTitle": "Task timeout updated",
  "settings.user.timeout.savedDesc": "Takes effect from the next task — no need to restart Revit.",
  "settings.user.timeout.saveErrorTitle": "Failed to update task timeout",
  "message.saveSkill": "Save Skill",
  "toast.toolSaveSuccess": "The skill saved successfully.",
  "toast.toolSaveError": "Error saving skill",
  "skills.saveNeedsYear": "This code did not run against a known Revit version, so it cannot be saved as a skill.",
  "starterPrompts.generic.overview": "Explain the levels and spaces in the current model",
  "starterPrompts.generic.count": "Count elements in the current model by category",
  "starterPrompts.generic.export": "Export a basic overview of the current model to CSV",

  // Composer controls — wording from `bimgo-ai-web`'s messageInput.* keys.
  "messageInput.modeLabel": "Mode",
  "messageInput.mode.hitl": "Review",
  "messageInput.mode.auto": "Auto",
  "messageInput.mode.plan": "Plan",
  "messageInput.modeDesc.hitl": "Confirm important steps before they run",
  "messageInput.modeDesc.auto": "Complete tasks automatically without step-by-step approval",
  "messageInput.modeDesc.plan": "Propose a complete plan and execute it after approval",
  "messageInput.modelLabel": "Model",
  "messageInput.reasoningLabel": "Reasoning",
  "messageInput.reasoningReset": "Reset effort",
  "messageInput.reasoning.none": "None",
  "messageInput.reasoning.low": "Low",
  "messageInput.reasoning.medium": "Medium",
  "messageInput.reasoning.high": "High",
  "messageInput.reasoning.xhigh": "Extra high",
  "messageInput.reasoning.max": "Max",
  "messageInput.reasoningDesc.none": "Answer at once without extra thinking — fastest, for simple lookups",
  "messageInput.reasoningDesc.low": "Think briefly — quick answers for routine tasks",
  "messageInput.reasoningDesc.medium": "Balanced thinking — the default for most work",
  "messageInput.reasoningDesc.high": "Think it through — slower and costlier, for complex modelling",
  "messageInput.reasoningDesc.xhigh": "Think at length — newest models only; others run at High",
  "messageInput.reasoningDesc.max": "Think as long as it takes — newest models only; others run at High",
  "messageInput.reasoningHint": "Higher effort means more thorough responses, but takes longer and uses your limits faster.",
  "messageInput.attach": "Attach an image, PDF, spreadsheet, or DWG",
  "messageInput.removeAttachment": "Remove attachment",
  // Shown on a sent attachment whose URL carried no filename to read.
  "messageInput.attachment": "Attachment",
  // `bimgo-ai-web`'s generic tool label, verbatim. Referenced by `hitlCard.ts` as a
  // fallback long before it existed here, so an approval card for a skill that named
  // itself nothing rendered the literal string "message.toolUseTool" on screen.
  "message.toolUseTool": "Tool",
  // CSV previews in the transcript (#58 item 2). Wording from `bimgo-ai-web`'s
  // `csv.*` keys; `csv.dimensions` and `csv.empty` are 2.5's own — the reference
  // built the first inline and had no message for the second.
  // The mention parameter picker (#58 item 3). Key names and wording from
  // `bimgo-ai-web`'s `mention.params.*`; `title` is 2.5's own, for the dialog's
  // accessible name, which the reference has no equivalent for.
  "mention.params.title": "Parameters of {name}",
  "mention.params.loading": "Loading parameters...",
  "mention.params.empty": "No parameters available",
  "mention.params.instanceTitle": "Instance Parameters",
  "mention.params.typeTitle": "Type Parameters",
  "mention.params.familyTitle": "Family Parameters",
  "mention.params.cadLayerTitle": "CAD Layers",
  "mention.params.cadBlockTitle": "CAD Blocks",
  "mention.params.clearAll": "Clear",
  "mention.params.searchPlaceholder": "Search parameters...",
  "mention.params.searchEmpty": "No matching parameters",
  "csv.title": "CSV File",
  "csv.loading": "Loading CSV data...",
  "csv.loadFailed": "Failed to load CSV: {error}",
  "csv.downloadFile": "Download the file",
  "csv.download": "Download",
  "csv.showPreview": "Show Preview",
  "csv.showFullTable": "Show Full Table",
  "csv.showingRows": "Showing {shown} of {total} data rows.",
  "csv.viewAllRows": "View all rows",
  "csv.dimensions": "{rows} rows × {cols} columns",
  "csv.empty": "No data found in CSV",

  "messageInput.placeholder": "Type a message",
  "mention.noResults": "No results found",
  "revitIndex.title": "Revit Database",
  "revitIndex.open": "Open Revit Database",
  "revitIndex.close": "Close Revit Database",
  "revitIndex.back": "Back to categories",
  "revitIndex.refresh": "Refresh index",
  "revitIndex.searchPlaceholder": "Search Revit elements...",
  "revitIndex.loading": "Loading index...",
  "revitIndex.missing": "No index yet",
  "revitIndex.missingDescription": "Build the index to browse and insert Revit elements.",
  "revitIndex.build": "Build index",
  "revitIndex.retry": "Retry",
  "revitIndex.building": "Building Revit Database...",
  "revitIndex.failed": "Index build failed",
  "revitIndex.failedDescription": "The Revit Database could not be built. Try again.",
  "revitIndex.unavailable": "Revit Database unavailable",
  "revitIndex.unavailableDescription": "No document is open in the connected Revit.",
  "revitIndex.staleWarning": "The model changed. Refresh to update the Revit info.",
  "revitIndex.noResults": "No matching elements",
  "revitIndex.updated": "Updated {time}",
  "revitIndex.dragHint": "Click to insert, or drag to the message input",
  "revitIndex.dropToInsert": "Drop to insert Revit item",
  "projects.currentProject": "Current project: {name}",
  "chat.welcomeMessage": "Hello, I am your AEC AI Expert. What can I do for you today?",
  "chat.welcomeTitle": "Hello, I am your AEC AI Expert.",
  "chat.welcomeSubtitle": "What can I do for you today?",
  "chat.pluginSetup.title": "Connect your AEC software",
  "chat.pluginSetup.description": "Install the Bimgo AI plugin to work with your Revit models or AutoCAD drawings.",
  "chat.pluginSetup.revit": "Set up Revit",
  "chat.pluginSetup.autocad": "Set up AutoCAD",
  "chat.pluginSetup.configured": "{host} configured",
  "chat.pluginSetup.checking": "Checking connections…",
  "chat.pluginLaunch.title": "Open {host}",
  "chat.pluginUpdate.title": "{host} plugin update available",
  "chat.pluginUpdate.rollbackTitle": "{host} plugin rollback available",
  "chat.pluginUpdate.description": "Update your plugin now to ensure features work properly.",
  "chat.pluginUpdate.action": "Update now",
  "chat.pluginUpdate.actionLabel": "Update {host} plugin now",
  "chat.pluginLaunch.description": "Once opened, Bimgo AI will connect automatically and start working with you.",
  "chat.pluginLaunch.eitherHost": "Revit or AutoCAD",
  "chat.pluginLaunch.waiting": "Waiting for your CAD software to connect",
  "chat.send": "Send",
  "chat.stop": "Stop",
  "chat.stoppedNotice": "You stopped this response.",
  "chat.thinking": "Working…",
  "chat.copyMessage": "Copy message",
  "chat.copied": "Copied",
  "chat.error.reference": "Reference",
  "chat.error.copyReference": "Copy reference",
  "chat.signInFirst": "Sign in and pick a project to start a conversation.",
  "chat.instanceInUse": "{host} is busy with another chat. Wait for it to finish, or stop it there, before sending here.",
  "chat.showReasoning": "Show reasoning",
  "chat.hideReasoning": "Hide reasoning",
  "message.showSourceCode": "Show Source Code",
  "message.copyCode": "Copy code",
  "message.codeCopied": "Copied",
  "common.failed": "Failed",
  "message.toolDidNotWork": "Didn't work",
  "cad.changes": "Comparison differences",
  "cad.kind.added": "Added",
  "cad.kind.deleted": "Deleted",
  "cad.kind.moved": "Moved",
  "cad.kind.text": "Text",
  "cad.kind.mixed": "Multiple changes",
  "cad.kind.modified": "Modified",
  "cad.annotations.completed": "Annotations completed",
  "cad.annotations.incomplete": "Annotations incomplete",
  "cad.annotations.not_requested": "Results only; annotations not requested",
  "cad.zooming": "Moving the view…",
  "cad.zoomFailed": "Couldn't move the AutoCAD view. Wait until AutoCAD is idle, then try the change again.",
  "cad.zoomWrongDrawing": "Return to the drawing used for this comparison, or compare again.",
  "common.stopped": "Stopped",
  "common.calling": "Calling",
  "common.thinking": "Thinking...",
  "message.toolGenerateCode": "Generate Code",
  "message.toolLookUpDocs": "Look Up Revit Docs",
  "message.toolFilterElements": "Filter Elements",
  "message.toolInspectModel": "Inspect Model with Code",
  "message.toolGetCurrentView": "Get Current View",
  "message.toolGetSelectedElements": "Get Selected Elements",
  "message.toolGetFamilyTypes": "List Family Types",
  "message.toolHighlightElements": "Highlight Elements",
  "message.toolColorSplash": "Color Elements",
  "message.toolCreateSectionBox": "Create Section Box",
  "message.toolRoomCollision": "Analyze Room Collisions",
  "message.toolRoadwayClearance": "Check Roadway Clearance",
  "message.toolWallTopCapping": "Check Wall Top Capping",
  "message.toolPlaceholderFamilies": "Prepare Placeholder Families",
  "message.toolFindRoadwayFloor": "Find Roadway Floors",
  "message.toolDraftCode": "Draft Code",
  "message.toolLoadSkill": "Load Skill",
  "message.toolSaveSkill": "Save Skill",
  "message.toolReadLibrary": "Read Skills Library",
  "message.toolSubmitPlan": "Submit Plan",
  "message.toolAskUser": "Ask a Question",
  "chat.contextMeterLabel": "Context used",
  "chat.contextMeterTooltip": "This conversation has used {percent}% of the model context.",
  "chat.contextMeterCriticalTooltip":
    "This conversation has used {percent}% of the model context. Start a new conversation to continue — the next message may not go through.",
  // The ask_user card's strings are `hitl.*`, the web client's keys and wording (#99).

  "common.history": "History",
  "workflow.toolsBar": "Workflow tools",
  "workflows.title": "Workflows",
  "workflows.filter.label": "Category",
  "workflows.filter.all": "All categories",
  "workflows.sort.label": "Sort",
  "workflows.sort.category": "Category",
  "workflows.sort.available": "Available first",
  "workflows.sort.name": "Name",
  "workflows.empty": "No workflow matches.",
  "workflows.new": "New workflow",
  "workflows.comingSoon": "Coming soon",
  "workflows.start": "Start",
  "workflows.running": "Running…",
  "workflows.starting": "Starting the run…",
  "workflows.continue.stopped": "This run stopped before it finished.",
  "workflows.continue.action": "Continue the run",
  "workflows.continue.destructive": "{tools} may already have changed the model or the files. Check the result, then continue, or send a new message instead.",
  "workflows.continue.confirm": "Continue anyway",
  "workflows.continue.stoppedAt": "Stopped at: {step}",
  "workflows.continue.stoppedThinking": "Stopped while the agent was working out the next step.",
  "workflows.category.interoperability": "Interoperability",
  "workflows.category.modeling": "Modeling",
  "workflows.category.domain": "Domain experts",
  "workflows.unsupported": "Update the desktop app to use workflows.",
  "workflows.unavailable": "The workflow list could not be read from the agent.",
  "workflows.notReady.hostNotConnected": "Connect {host} to start.",
  "workflows.notOpen": "This workflow is not available in this desktop version, so the conversation cannot continue here.",
  "chat.loading": "Loading...",
  "chat.noThreads": "No chat history yet",
  "chat.newConversation": "New Conversation",
  "chat.threadNotFound": "This conversation was not found, or you do not have access to it",
  "chat.projectAccessLost":
    "You no longer have access to this project. Please contact a project admin.",

  // Projects — wording, order and states mirror `bimgo-ai-web`'s
  // `ProjectsSection` / `ProjectsList`, so the two clients read identically.
  "projects.title": "Projects",
  "projects.projectList": "Projects",
  "projects.projectCount": "{count} projects",
  "projects.searchPlaceholder": "Search projects by name, description, or notes...",
  "projects.loadingProjects": "Loading projects...",
  "projects.loadProjectsFailed": "Failed to load projects",
  "projects.retry": "Retry",
  "projects.noMatchingProjects": "No matching projects found",
  "projects.adjustFilter": "Try adjusting your search or filter criteria",
  "projects.noProjects": "No projects yet",
  "projects.createFirstProject": "Create your first project",
  "projects.noProjectsAssigned": "You have not been assigned to any projects yet",
  "projects.contactProjectAdmin":
    "Contact a project admin or organization admin to be added to a project",
  "projects.columnName": "Project Name",
  "projects.columnDescription": "Description",
  "projects.columnStatus": "Status",
  "projects.columnCreatedAt": "Created",
  "projects.columnActions": "Actions",
  "projects.statusActive": "Active",
  "projects.statusInactive": "Inactive",
  "projects.notes": "Notes: {notes}",
  "projects.noDescription": "No description",
  "projects.edit": "Edit",
  "projects.delete": "Delete",
  "projects.members": "Members",

  // Create / edit project dialog
  "projects.createProject": "Create Project",
  "projects.editProject": "Edit Project",
  "projects.createProjectDesc":
    "Fill in the project details to create a new project. Project name is required.",
  "projects.editProjectDesc": "Modify the project details. Project name is required.",
  "projects.projectName": "Project Name *",
  "projects.projectNamePlaceholder": "Enter project name",
  "projects.projectNameRequired": "Project name is required",
  "projects.projectDescription": "Description",
  "projects.projectDescriptionPlaceholder": "Enter project description (optional)",
  "projects.projectDescriptionHint": "Briefly describe the project content and goals",
  "projects.projectNotes": "Notes",
  "projects.projectNotesPlaceholder": "Enter notes (optional)",
  "projects.projectNotesHint": "Other relevant project information",
  "projects.cancel": "Cancel",
  "projects.creating": "Creating...",
  "projects.updating": "Updating...",
  "projects.updateProject": "Update Project",

  // Delete project dialog
  "projects.confirmDelete": "Confirm Delete Project",
  "projects.deleteWarning":
    "This action cannot be undone. Please confirm you want to delete this project.",
  "projects.projectNameLabel": "Project Name:",
  "projects.descriptionLabel": "Description:",
  "projects.confirmDeleteBtn": "Confirm Delete",

  // Desktop-only: this shell binds one project to the chat, which the web
  // client has no equivalent of.
  "projects.noOrg": "No organization selected.",
  "projects.selected": "Selected",
  "projects.openChat": "Open this project’s chat",
  "projects.noneSelected": "No project selected — pick one in Projects",

  // Project members
  "projectMembers.title": "Manage Project Members",
  "projectMembers.assignTitle": "Assign Members",
  "projectMembers.searchMember": "Search members...",
  "projectMembers.noMemberFound": "No matching members",
  "projectMembers.allAssigned": "All organization members are assigned",
  "projectMembers.add": "Assign",
  "projectMembers.empty": "No members assigned yet",
  "projectMembers.loadFailed": "Failed to load project members",
  "projectMembers.addFailed": "Failed to assign member",
  "projectMembers.selectAll": "Select all",
  "projectMembers.selectMember": "Select member",
  "projectMembers.selectedCount": "Selected",
  "projectMembers.clearSelection": "Clear",
  "projectMembers.removeSelected": "Remove",
  "projectMembers.removeConfirmTitle": "Remove selected members",
  "projectMembers.removeConfirmDescPrefix": "Remove the selected",
  "projectMembers.removeConfirmDescSuffix":
    "member(s) from this project? They will lose access unless they are an organization admin or the project creator.",
  "projectMembers.bulkRemoveFailed": "Failed to remove the selected members",
  "projectMembers.removeOrgAdminBlocked":
    "Organization admins have implicit project access and cannot be removed here",
  "projectMembers.implicitAccessNote":
    "Organization admins have access to every project, so their assignment cannot be edited here; the project creator always has access and may not appear in this list.",

  // Account card (2.5-specific)
  "settings.pane.account": "Account",
  "settings.pane.email": "Email",
  "settings.pane.organization": "Organization",
  "settings.pane.about": "About",
  "settings.pane.desktop": "Desktop",
  "settings.pane.bridge": "Bridge",
  "settings.pane.environment": "Environment",
  "settings.pane.platform": "Platform",
  "settings.pane.noBridge": "No desktop bridge — build identity unavailable.",
  "settings.pane.notSignedIn": "not signed in",

  // Update password
  // The grace-period banner (#75) — the first half of the message whose second
  // half is `login.signedOutElsewhere`. Wording verbatim from `bimgo-ai-web`.
  "auth.signingOutSoon":
    "Your account was signed in on another device. This device will be signed out shortly.",
  "login.signedOutElsewhere":
    "Your account was signed in on another device, so this device was signed out. An account can only be used on one device at a time.",
  // A revocation nothing explains (#124). Claiming another device here was the
  // bug: GoTrue's codes cannot tell a newer login from an expired or rotated
  // token, so this is what a sign-out with no peer announcement says.
  "login.sessionEnded": "Your session has ended. Please sign in again.",
  "login.email": "Email",
  "login.emailPlaceholder": "Enter your email address",
  "login.password": "Password",
  "login.passwordPlaceholder": "Enter your password",
  "login.rememberMe": "Remember me",
  "login.signIn": "Sign in",
  "login.signingIn": "Signing in…",
  "login.forgotPassword": "Forgot password?",
  "login.backToSignIn": "Back to sign in",
  "login.resetTitle": "Reset your password",
  "login.resetSubtitle": "We'll email you a link to set a new one.",
  "login.sendResetLink": "Send reset link",
  "login.sending": "Sending…",
  "login.resetSent":
    "If an account exists for that email, a reset link is on its way. It opens in your browser.",
  // The recovery landing (#21). The Settings card reuses `title`, `placeholder`,
  // `updating`, `confirm` and `successDesc`; the rest belong to the standalone
  // page a reset link opens in a browser.
  "updatePassword.subtitle": "Choose a new password for your Bimgo AI account.",
  "updatePassword.newLabel": "New password",
  "updatePassword.confirmLabel": "Confirm new password",
  "updatePassword.confirmPlaceholder": "Enter it again",
  "updatePassword.mismatch": "The two passwords do not match.",
  "updatePassword.checking": "Checking your link…",
  // The confirmation, wording from `bimgo-ai-web`'s own UpdatePassword page.
  // `newUserDesc` and the button name the **desktop app**, not the Revit plugin
  // the web client points at — same flow, different product.
  "updatePassword.accountReadyTitle": "Your account is ready",
  "updatePassword.accountReadyDesc": "Your password has been set successfully.",
  "updatePassword.openDesktop": "Open Bimgo AI 2.0",
  "updatePassword.doneReturnToApp":
    "You can close this tab and sign in with it in the Bimgo AI 2.0 app.",
  "updatePassword.newUserTitle": "New to Bimgo AI?",
  "updatePassword.newUserDesc":
    "Install the Bimgo AI 2.0 app to get started — the installation guide is in our documentation.",
  "updatePassword.installDesktop": "Installation guide",
  "updatePassword.signOutError":
    "Your password was set, but the temporary session could not be ended safely. Close this page and try again.",
  "updatePassword.invalidTitle": "This link cannot be used",
  "updatePassword.invalidDesc":
    "The reset link has expired or has already been used.",
  "updatePassword.invalidRetry":
    "Request a new one from the sign-in screen in the Bimgo AI 2.0 app.",
  "updatePassword.title": "Update Password",
  "updatePassword.placeholder": "Enter new password (minimum 6 characters)",
  "updatePassword.updating": "Updating…",
  "updatePassword.confirm": "Confirm",
  "updatePassword.errorTitle": "Error updating password",
  "updatePassword.successDesc": "Your password has been updated. It takes effect on your next sign-in.",
  "updatePassword.unconfigured": "Password change needs a configured Supabase project.",
  // The click-to-verify step a reset link lands on first; wording from `bimgo-ai-web`.
  "authConfirm.resetTitle": "Reset your password",
  "authConfirm.desc": "Click the button below to continue and set your password.",
  "authConfirm.continue": "Continue",

  // Roles card
  "settings.user.roles.none": "No roles assigned.",
  "settings.user.roles.viewPermissions": "View Permissions",

  // Language
  "settings.user.language.title": "Language",
  "settings.user.language.en": "English",
  "settings.user.language.zh": "中文",
  "settings.user.language.restartNote":
    "The app updates instantly. The desktop app's own prompts change language after a restart.",

  // Diagnostics (desktop only) — wording mirrors v2's `SupportLogs.*`
  "settings.diagnostics.packaged": "Logs packaged",
  "settings.diagnostics.packagedBody": "{count} log file(s) → {path}",
  "settings.diagnostics.contains":
    "The zip contains all local BimGo log files from the last 7 days, the install and session ids and the app version — never your documents, credentials or the local database. Please send this file to BIMGO support.",
  "settings.diagnostics.skipped": "{count} file(s) were in use and skipped.",
  "settings.diagnostics.failed": "Failed to package logs: {error}",

  // Permissions dialog
  "settings.user.actions.title": "Your Permissions",
  "settings.user.actions.none": "No permissions available.",
  "settings.user.actions.feature.org": "Organization",
  "settings.user.actions.feature.project": "Project",
  "settings.user.actions.feature.skill": "Skill",
  "settings.user.actions.feature.chat": "Chat",
  "settings.user.actions.action.org.read": "View All",
  "settings.user.actions.action.org.update": "Update",
  "settings.user.actions.action.org.manage_members": "Manage Members",
  "settings.user.actions.action.org.manage_roles": "Manage Roles",
  "settings.user.actions.action.org.manage_secrets": "Manage Secrets",
  "settings.user.actions.action.org.read_secret": "View",
  "settings.user.actions.action.org.read_members": "View Members",
  "settings.user.actions.action.project.create": "Create",
  "settings.user.actions.action.project.read": "View",
  "settings.user.actions.action.project.update": "Update",
  "settings.user.actions.action.project.delete": "Delete",
  "settings.user.actions.action.project.manage_members": "Manage Members",
  "settings.user.actions.action.skill.create": "Create",
  "settings.user.actions.action.skill.read": "View",
  "settings.user.actions.action.skill.edit": "Edit",
  "settings.user.actions.action.skill.delete": "Delete",
  "settings.user.actions.action.skill.publish": "Publish",
  "settings.user.actions.action.skill.download": "Download",
  "settings.user.actions.action.skill.run": "Run",
  "settings.user.actions.action.skill.enable": "Enable",
  "settings.user.actions.action.chat.ask": "Ask",

  // Org members table
  "settings.org.members.title": "Organization Members",
  "settings.org.members.colMember": "Member",
  "settings.org.members.colRoles": "Roles",
  "settings.org.members.colActions": "Actions",
  "settings.org.members.edit": "Edit",
  "settings.org.members.view": "View permissions",
  "settings.org.members.empty": "No members in this organization yet.",
  "settings.org.members.loadFailed": "Failed to load members. Please try again later.",
  "settings.org.members.searchPlaceholder": "Search by email or role…",
  "settings.org.members.editDialog.title": "Edit Member Roles",
  "settings.org.members.editDialog.save": "Save",
  "settings.org.members.editDialog.saving": "Saving…",
  "settings.org.members.editDialog.cancel": "Cancel",
  "settings.org.members.editDialog.successTitle": "Roles updated",
  "settings.org.members.editDialog.errorTitle": "Update failed",

  // Role labels
  "settings.org.members.role.organization_admin": "Organization Admin",
  "settings.org.members.role.project_admin": "Project Admin",
  "settings.org.members.role.advance_user": "Advance User",
  "settings.org.members.role.standard_user": "Standard User",
  "settings.org.members.roleDesc.organization_admin":
    "Full organization management: members, roles, billing, plus all project and skill operations",
  "settings.org.members.roleDesc.project_admin":
    "Manage projects (create, update, delete) and all skill operations",
  "settings.org.members.roleDesc.advance_user":
    "Create, edit, delete, and publish skills, plus standard user permissions",
  "settings.org.members.roleDesc.standard_user":
    "Basic access: chat, view projects, download and run skills",
  // Skills pane — wording from `bimgo-ai-web`'s skills.* / toolEditor.* /
  // skillVersions.* / skillHistory.* / toast.* keys, plus the target gate
  // (item BD), which the reference never needed with one Revit per agent.
  "skills.title": "Skills",
  "skills.subtitle": "Saved skills on this computer, grouped by software vendor and version. Running a skill needs a connected instance of its version.",
  "skills.refresh": "Refresh",
  "skills.agentUnavailable": "The local agent is not reachable, so the skill store cannot be read.",
  "skills.fetchError": "Error fetching skills",
  "skills.pluginOutdated": "Agent Version Outdated",
  "skills.pluginOutdatedDesc": "This desktop's agent does not serve the skills feature. Update to the latest version and try again.",
  // The whole pane when nothing of this vendor is running. Skills are stored, edited and
  // run per instance, so with none open there is no year to scope to and nothing worth
  // listing — this says what to do about it instead.
  // Revit by name, not "a CAD service" (owner): it is the only one beta serves, and a
  // message telling the user to act has to name the thing they are meant to open.
  //
  // **Per Revit version, not per instance.** The store really is keyed by version, two
  // windows of the same Revit share it, and "instance" is our word for a process — it means
  // nothing to someone looking at an empty page. The version is also what the user sees:
  // it is what the pill on the title row reads and what every card is badged with.
  //
  // Two paragraphs, not one (owner): the first says how skills are organised, the second
  // says what to do next. Run together they read as one long sentence in which the
  // instruction — the only part the user has to act on — is buried at the end.
  "skills.noInstance": "Open your design software to use Skills",
  "skills.vendors.label": "Software",
  "skills.autocad.newFolderDesc": "Create a Skill folder for your AutoCAD skills on this computer.",
  "skills.autocad.years": "Supported on AutoCAD",
  "skills.crossVersion.prompt": "Build this skill for other AutoCAD versions.",
  "skills.crossVersion.unsupported": "This skill does not support AutoCAD {version}. Select a supported AutoCAD in the title bar to run or edit it.",
  "skills.crossVersion.unsupportedChip": "Does not support AutoCAD {version}",
  "skills.crossVersion.buildOthers": "Build for other versions",
  "instance.unsupportedBySkill": "The open skill does not support AutoCAD {version}. Build it for {version} from a supported version first.",
  "skills.crossVersion.buildOthersHint": "The agent asks which AutoCAD versions to add, checks the code on each one that is open, and asks you to confirm before saving.",
  "skills.crossVersion.chatBlocked": "Not available for AutoCAD {version}. Select a supported AutoCAD in the title bar.",
  "skills.versionGone.title": "This version does not support AutoCAD {year}",
  "skills.versionGone.desc": "v{version} supports only AutoCAD {versions}, so it can't run or be edited in the connected AutoCAD {year}.",
  "skills.versionGone.back": "Back to skills",
  "skills.noInstanceDesc": "Skills are saved per software and version, such as Revit or AutoCAD.",
  "skills.noInstanceHint": "Open the software you work in, then select it in the title bar to see its skills.",
  "skills.noSkillsForVersion": "No skills for this version yet. Save one from a chat against this instance.",
  "skills.searchPlaceholder": "Search skills by name or description",
  "skills.noMatch": "No skills match that search.",
  "skills.savedTab": "Saved",
  "skills.mySkills": "My Skills",
  "skills.sharedTab": "Organization",
  "skills.saved": "Saved",
  "skills.download": "Download",
  "skills.downloadSuccess": "Skill downloaded to this computer.",
  "skills.downloadError": "Download failed",
  "skills.noSharedSkills": "No Shared Skills Yet",
  "skills.noSharedSkillsDesc": "No one has shared a skill with your organization yet.",
  "skills.sharedLoadError": "Could not load the organization's skills.",
  "skills.noSharedForVersion": "Nothing shared for {version} yet.",
  "skills.deleteSuccess": "Skill deleted",
  "skills.deleteError": "Failed to delete skill",
  "skills.deleteCloud": "Remove from the organization",
  // Why Publish is disabled on a downloaded copy: publishing would not update the row it came
  // from, it would file a second one under your name.
  "skills.publishNotOwner": "This Skill was published by someone else. Only its publisher can publish a new version of it.",
  "skills.deleteCloudTitle": "Remove this skill from the organization?",
  "skills.deleteCloudDesc": "Members will no longer see or download it. Copies already downloaded stay on their computers; yours reads as never published.",
  "toolEditor.details": "Details",
  "skills.published": "Published",
  "skills.newFolder": "New folder",
  "skills.newFolderDesc": "Create a Skill folder for {version} on this computer.",
  "skills.folderNamePlaceholder": "Folder name",
  "skills.createFolder": "Create",
  "skills.folderCreateSuccess": "Folder created",
  "skills.folderCreateError": "Failed to create folder",
  // Named with the year, because a folder belongs to one year's store and those are the
  // only ones this menu can offer.
  "skills.moveToTargetFolder": "Move to {target} folder",
  "skills.rootFolder": "Root",
  "skills.moveSuccess": "Skill moved",
  "skills.moveError": "Failed to move skill",
  "skills.folderEmpty": "There are no Skills in this folder yet.",
  "skills.skillCount": "{count} Skills",
  "skills.unknownVersion": "unknown version",
  "skills.connected": "an instance of this version is connected",
  "skills.run.noPermission": "You do not have permission to run skills (requires skill:run).",
  "skills.run.disabled": "This skill is disabled. Enable it to run.",
  "skills.run.unknownVersion": "This skill does not say which version it was written for, so it cannot be matched to an instance.",
  "skills.run.noInstance": "Select a connected {target} instance in the title bar to run this skill.",
  "skills.run.wrongInstance": "This skill targets {target}; the selected instance is {selected}. Select a {target} instance to run it.",
  "skills.deleteSkillTitle": "Delete Skill",
  "skills.deleteSkillDesc": "This removes the skill and its run history from this computer. This cannot be undone.",
  "skills.deleteAllVersions": "All {count} versions of this skill will be deleted: {versions}.",
  "skills.deleteOnlyVersion": "The skill's only version will be deleted: {versions}.",
  "skills.category.mep": "MEP",
  "skills.category.structure": "Structure",
  "skills.category.revitOps": "Revit operations",
  "skills.category.default": "General",
  "toolEditor.run": "Run",
  "toolEditor.running": "Running...",
  "toolEditor.parameters": "Parameters",
  "toolEditor.paramDefault": "Default",
  "toolEditor.noParameters": "This skill takes no parameters.",
  "toolEditor.selectValue": "Select a value",
  "toolEditor.result": "Result",
  "toolEditor.summary": "Summary",
  "toolEditor.resultDetails": "Result Details",
  "toolEditor.dataTables": "Data Tables",
  "toolEditor.warnings": "Warnings",
  "toolEditor.delete": "Delete",
  "toolEditor.code": "Code",
  "toolEditor.executedAt": "Last executed",
  "toolEditor.lines": "Lines",
  "toolEditor.characters": "Characters",
  "toolEditor.enabled": "Skill enabled",
  "toolEditor.enableSuccess": "Skill enabled",
  "toolEditor.disableSuccess": "Skill disabled",
  "toolEditor.toggleEnabledError": "Failed to update enabled state",
  "skillEdit.versionsTitle": "Versions",
  "skillEdit.chatTitle": "Edit with AI",
  "skillEdit.emptyState": "Describe the change you want — the agent revises this skill's code, asks before saving, and tags each save as a new version.",
  "skillEdit.inputPlaceholder": "Describe what to change in this skill…",
  "skillEdit.awaitingApprovalPlaceholder": "Waiting for your decision on the approval card…",
  "skillEdit.viewCode": "View code",
  "skillEdit.backToChat": "Back to chat",
  "skillEdit.currentThread": "Current",
  "skillEdit.titleCommit": "Save new skill version",
  "skills.edit.noPermission": "Your role does not allow editing skills.",
  // Says how long it may take: a run on a large model can take minutes, and a composer that is
  // simply closed with no sense of the wait reads as the editor having hung.
  "skills.edit.runInProgress": "This Skill is running, which can take a few minutes on a large model. Editing resumes when the run finishes.",
  "skills.edit.unknownVersion": "This skill does not say which version it was written for, so it cannot be edited.",
  "skills.edit.noInstance": "Editing tests changes against {target}. Select a connected {target} instance to edit this skill.",
  "skills.edit.wrongInstance": "This skill targets {target}; the selected instance is {selected}. Select a matching instance to edit it.",
  /**
   * The mismatch, said **once**, on the editor's title row (owner).
   *
   * `run.wrongInstance` and `edit.wrongInstance` are the same sentence twice, and the editor
   * showed both at once — under the Run button and under the composer. One notice beside the
   * title covers the page; the two controls stay disabled and say only what to do.
   */
  "skills.targetMismatch": "This Skill targets {target}; the selected instance is {selected}. Select a {target} instance to run or edit it.",
  "skills.targetMismatchChip": "Targets {target}",
  "skills.edit.mismatchShort": "Not available for the selected instance.",
  "skillRegenerate.successTitle": "Skill updated",
  "skillRegenerate.successDescription": "Saved as v{version}.",
  "skillRegenerate.testHint": "Run it once to confirm the change.",
  "skillVersions.switchBlockedHint": "Version switching is paused while the Skill is running, the agent is working, or a decision is pending.",
  "skillVersions.switchedTitle": "Version switched",
  "skillVersions.switchedDescription": "The working copy is now v{version}.",
  "skillVersions.switchError": "Could not switch versions.",
  "toolEditor.edit": "Edit",
  "toolEditor.update": "Update",
  "toolEditor.unsaved": "Unsaved",
  "toolEditor.saving": "Saving…",
  "toolEditor.done": "Done",
  "toolEditor.updateSuccess": "Skill updated.",
  "toolEditor.updateError": "Could not update the skill.",
  "toolEditor.namePlaceholder": "Skill name",
  "toolEditor.description": "Description",
  "toolEditor.descriptionPlaceholder": "What this skill does…",
  "toolEditor.noDescription": "No description yet.",
  "toolEditor.publish": "Publish",
  "toolEditor.publishing": "Publishing…",
  "toolEditor.publishSuccess": "Skill published to your organization.",
  "toolEditor.publishError": "Publish failed",
  "toolEditor.confirmPublishTitle": "Publish this skill?",
  "toolEditor.confirmPublishDesc": "The current version is shared with your organization in the cloud. Members will be able to download and run it.",
  "toolEditor.lockedDuringUpdate": "Locked while the agent is updating this skill.",
  "common.newThread": "New thread",
  // The sidebar rail. These were English literals in `AppShell` and did not
  // translate with the rest of the pane; `routes.ts` now holds keys, not words.
  "shell.nav.new": "New chat",
  "shell.nav.chat": "Chat",
  // The current project, at the head of the sidebar's list: a label over its name.
  "shell.project.label": "Project",
  "shell.project.none": "Select a project",
  // The sidebar's Chats section: what is running, then the project's newest few.
  "shell.chats.title": "Chats",
  "shell.chats.viewAll": "View all ({count})",
  // The Chats page behind "View all": every conversation of the project.
  "chats.title": "Chats",
  "chats.count": "{count} chats",
  "chats.searchPlaceholder": "Search chats by title",
  "chats.noMatches": "No chats match your search",
  "chats.group.today": "Today",
  "chats.group.yesterday": "Yesterday",
  "chats.group.week": "Previous 7 days",
  "chats.group.earlier": "Earlier",
  // The sidebar's way back to conversations still working or waiting (#193).
  "shell.running.untitled": "New chat",
  "shell.running.working": "Working",
  "shell.running.needsApproval": "Needs approval",
  "shell.running.editing": "Editing: {name}",
  "shell.running.untitledSkill": "skill",
  "shell.running.skillEditor": "Skill editor",
  "shell.account.notSignedIn": "Not signed in",
  "shell.account.noOrganization": "No organization",
  "shell.sidebarCollapse": "Collapse sidebar",
  "shell.sidebarExpand": "Expand sidebar",
  "common.historyLoading": "Loading conversation…",
  "toast.regenThreadNotYours": "That conversation belongs to another account on this computer, so it stays private. Start a new thread to continue.",
  "toast.regenThreadVerifyFailed": "Could not verify that conversation right now — try again.",
  "skillVersions.published": "Published",
  "skillVersions.current": "Current",
  "skillVersions.empty": "No versions saved yet.",
  "skillVersions.loadError": "Failed to load versions.",
  "skillVersions.viewing": "Viewing v{version} (read-only)",
  // The banner's wording, which is a warning rather than a statement: it appears only while a
  // run or an edit turn is in flight, and those act on the working copy, not on what is shown.
  "skillVersions.viewingWarning": "Viewing v{version} — Run and Edit act on the current version",
  "skillVersions.backToCurrent": "Back to current",
  "skillHistory.title": "Run History",
  "skillHistory.empty": "No executions yet",
  "skillHistory.emptyDesc": "Records will appear here after you run this skill.",
  "skillHistory.loadError": "Failed to load run history.",
  "skillHistory.detailLoadError": "Failed to load execution details.",
  "skillHistory.success": "Success",
  "skillHistory.failed": "Failed",
  "skillHistory.timedOut": "Timed out",
  "skillHistory.executedAt": "Executed at",
  "skillHistory.duration": "Duration",
  "skillHistory.revitVersion": "Revit version",
  "skillHistory.skillVersion": "Skill version",
  "skillHistory.parameters": "Parameters",
  "skillHistory.viewResult": "View full result",
  "skillHistory.resultTitle": "Execution Result",
  "toast.skillLoadError": "Error loading skill",
  "toast.skillRunSuccess": "Skill \"{name}\" executed successfully",
  "toast.skillRunError": "Error executing skill: {error}",
  "toast.skillDeleteSuccess": "Skill deleted",
  "toast.skillDeleteError": "Error deleting skill",
  "toast.missingParams": "Missing required parameters",
  "toast.missingParamsDesc": "Please provide values for: {params}",
  "common.cancel": "Cancel",
  "common.loading": "Loading...",
  "common.back": "Back",
  // The Revit Family Builder (#255), ported from bimgo-ai-web's familyAuthoring.* strings.
  "familyBuilder.nav": "Family Builder",
  "familyBuilder.title": "Revit Family Builder",
  "familyBuilder.chatTitle": "Edit with AI",
  "familyBuilder.details": "Family Details",
  "familyBuilder.parameters": "Parameters",
  "familyBuilder.summary": "Run Summary",
  "familyBuilder.summaryEmpty": "The agent run summary will appear here after the first build or revision.",
  "familyBuilder.emptyTitle": "Describe the Revit family you want to create or revise",
  "familyBuilder.emptyDescription": "Describe the intent directly, or attach images, orthographic drawings, and product catalogues as references.",
  "familyBuilder.inputPlaceholder": "Describe what you want to create or change…",
  "familyBuilder.newComponent": "New Component",
  "familyBuilder.newThread": "New Thread",
  "familyBuilder.noDescription": "No description yet.",
  "familyBuilder.listEmptyTitle": "No Revit components yet",
  "familyBuilder.listEmptyDescription": "Create your first component, then return here to refine it, adjust parameters, and review versions.",
  "familyBuilder.applyParameters": "Apply parameter changes",
  "familyBuilder.parametersApplied": "Parameters applied to the active Revit component.",
  "familyBuilder.fixExecutionError": "Ask Agent to fix",
  "familyBuilder.fixExecutionErrorPrompt": "Please fix this error from applying parameters:",
  "familyBuilder.fixParameterContractPrompt": "Please fix the component parameter interface contract:",
  "familyBuilder.parameterContractInvalid": "The parameter configuration is incomplete, so the controls cannot be displayed.",
  "familyBuilder.uncommittedChanges": "There are unfinished changes that are not included in the latest successful version.",
  "familyBuilder.continueUncommittedChanges": "Ask Agent to continue",
  "familyBuilder.uncommittedChangesPrompt": "Continue the unfinished component changes that are not included in the latest successful version. Inspect the current code.cs and SKILL.md, complete the necessary corrections, then Build and validate the result.",
  "familyBuilder.versioningPartialSuccess": "RFA updated, but version history was not saved",
  "familyBuilder.versionSaveRepairPrompt": "The component was updated, but its version was not saved. Inspect the current code.cs, metadata.json, and SKILL.md, make any necessary corrections, then Build again and confirm that the version is saved.",
  "familyBuilder.viewRefreshFailed": "The component was updated, but the editor could not refresh. Reopen this component to load the latest content.",
  "familyBuilder.activeRfaRequired": "Switch back to this family's original RFA in Revit, then send a new request. This operation was not executed.",
  "familyBuilder.reopenRfaRequired": "The original family document is closed or unavailable. Reopen it here, then send a new request.",
  "familyBuilder.reopenRfa": "Reopen original family",
  "familyBuilder.attach": "Add references",
  "familyBuilder.send": "Send",
  "familyBuilder.stop": "Stop",
  "familyBuilder.stopped": "Run stopped.",
  "familyBuilder.evidence": "3D validation screenshot",
  "familyBuilder.missingSession": "Your session has expired. Please sign in again.",
  "familyBuilder.rfaActivating": "Opening and activating this component’s RFA…",
  "familyBuilder.rfaBindingRequired": "This component has no linked RFA. Locate its existing RFA file.",
  "familyBuilder.rfaNotFound": "This component’s RFA was not found. Select its current file location.",
  "familyBuilder.rfaConflict": "The recorded RFA belongs to another BimGo AI component. Opening and Build are blocked.",
  "familyBuilder.rfaUnsaved": "This component’s RFA has not been saved.",
  "familyBuilder.rfaSaveFailed": "The current RFA could not be saved. Check that the file is writable.",
  "familyBuilder.rfaActivationFailed": "This component’s RFA is not confirmed ready. See the details below.",
  "familyBuilder.rfaBusy": "Finish the current Revit operation or close its dialog, then retry opening the RFA.",
  "familyBuilder.rfaOpenCancelled": "Opening the RFA was cancelled.",
  "familyBuilder.rfaRequestExpired": "The opening request expired. Check Revit before retrying manually.",
  "familyBuilder.rfaFocusPending": "The RFA is open. Switching to the 3D view has been requested.",
  "familyBuilder.rfaFocusFailed": "The RFA is open, but the 3D view could not be activated. You can continue working on the family.",
  "familyBuilder.rfaNotReady": "Open this component’s RFA in Revit to continue.",
  "familyBuilder.instanceBusy": "This Revit is busy with another task (a chat or a skill). Wait for it to finish, then retry.",
  "familyBuilder.openRfaInRevit": "Open in Revit",
  "familyBuilder.locateRfa": "Locate RFA",
  "familyBuilder.restoreNote": "Restoring a version brings back its code only. Build again to update the RFA in Revit.",
  "familyBuilder.restored": "Restored v{version}. Build again to update the RFA in Revit.",
  "familyBuilder.noRevit": "Open Revit to use the Family Builder",
  "familyBuilder.noRevitDesc": "Select Revit in the title bar to see the families saved for that version.",
  "familyBuilder.noRevitHint": "Select the Revit you want in the title bar.",
  "familyBuilder.notRevit": "The Family Builder works with Revit only",
  "familyBuilder.unsupported": "This version of the local agent has no Family Builder — update the desktop app.",
  "familyBuilder.activity.preparing": "Preparing the family authoring workspace",
  "familyBuilder.activity.reviewingArtifacts": "Reviewing the current family artifacts",
  "familyBuilder.activity.updatingArtifacts": "Updating the family specification and run summary",
  "familyBuilder.activity.editingCode": "Authoring and validating the family code",
  "familyBuilder.activity.inspecting": "Inspecting the Revit family",
  "familyBuilder.activity.building": "Building and visually validating the family",
  "familyBuilder.activity.preparingSummary": "Agent is working",
  "familyBuilder.activity.generic": "Running a family authoring step",
};

const zh: Dict = {
  "workflows.beta.enableTitle": "啟用{name}？",
  "workflows.beta.enable": "啟用 Beta 功能",
  "workflows.beta.warning": "此功能目前為 Beta 測試版本，可能出現錯誤或非預期結果，並且需要較長時間執行，使用前請謹慎評估。",
  "settings.beta.title": "Beta 功能",
  "settings.beta.description": "試用開發中的功能，可隨時關閉。",
  "settings.beta.empty": "目前沒有可試用的 Beta 功能。",
  "settings.beta.unavailable": "請連線至本機 Agent，以管理 Beta 功能。",
  "settings.beta.saveFailed": "設定未能儲存，請再試一次。",
  "skillPatch.count": "{count} 處修改",
  "skillPatch.preparing": "準備修改中",
  "skillPatch.ready": "修改已準備",
  "skillPatch.failed": "準備失敗",
  "skillPatch.stopped": "已停止",
  "skillPatch.unconfirmed": "結果未確認",
  "skillPatch.readOnly": "唯讀預覽。準備修改不會執行或儲存 Skill。",
  "skillPatch.diff": "程式碼修改差異",
  "skillPatch.operation": "修改 {index}",
  "skillPatch.parameters": "參數變更",
  "skillPatch.added": "新增",
  "skillPatch.removed": "移除",
  "skillPatch.modified": "修改",
  "skillPatch.before": "原本",
  "skillPatch.after": "修改後",
  "skillPatch.field": "欄位",
  "skillPatch.field.name": "名稱",
  "skillPatch.field.displayName": "顯示名稱",
  "skillPatch.field.type": "型別",
  "skillPatch.field.description": "說明",
  "skillPatch.field.required": "必填",
  "skillPatch.field.default": "預設值",
  "skillPatch.field.position": "順序",
  "skillPatch.field.enum": "選項",
  "skillPatch.field.minimum": "最小值",
  "skillPatch.field.maximum": "最大值",

  "skillEdit.titlePrepareChanges": "準備修改",
  // 工作日誌
  "common.showMore": "展開",
  "common.showLess": "收合",
  "workJournal.title": "工作日誌",
  "workJournal.empty": "這個對話還沒有工作紀錄。",
  "workJournal.failed": "未成功",
  "workJournal.unconfirmed": "結果未確認",
  "workJournal.stepCount": "{count} 個步驟",
  "workJournal.failedCount": "{count} 未成功",
  "workJournal.unconfirmedCount": "{count} 未確認",
  "workJournal.start": "較早的紀錄",
  "workJournal.ask": "請 Agent 說明這一步",
  "workJournal.ref": "工作日誌第 {n} 筆",
  "workJournal.question": "請說明{ref}「{name}」做了什麼、結果如何。",
  "workJournal.viewImage": "查看圖片",
  "workJournal.viewImages": "查看 {count} 張圖片",
  "workJournal.imageFailed": "圖片無法載入。",
  "workJournal.continued": "從這裡繼續執行",
  // 工作成果
  "endReport.title": "工作成果",
  "endReport.current": "本輪",
  "endReport.resolved": "本輪解決：{items}",
  "endReport.listJoin": "、",
  "endReport.view": "查看成果",
  "endReport.round": "第 {round} 輪",
  "endReport.roundOf": "第 {round} 輪 / 共 {total} 輪",
  "endReport.rounds": "報告輪次",
  "endReport.previous": "上一輪",
  "endReport.next": "下一輪",
  "endReport.status.done": "已完成",
  "endReport.status.partial": "部分完成",
  "endReport.status.not_done": "尚未完成",
  "endReport.stop.limit": "這一輪先做到這裡，可以從這裡接著做。",
  "endReport.stop.error": "過程中遇到問題先停下，已完成的部分都保留著。",
  "endReport.priority.high": "優先處理",
  "endReport.priority.medium": "建議處理",
  "endReport.priority.low": "之後再看",
  "endReport.results": "成果",
  "endReport.unchecked": "未檢查",
  "endReport.more": "另外 {count} 項",
  "endReport.restInList": "另外 {count} 處在完整清單中",
  "endReport.measureOf": "{done} / {expected} {unit}",
  "endReport.issues": "待辦",
  "endReport.count": "{count} 處",
  "endReport.elementIds": "Revit 元素 ID",
  "endReport.nextSteps": "下一步",
  "endReport.chosen": "你當時選擇",
  "endReport.recommended": "建議",
  "endReport.continue": "繼續處理",
  "endReport.continueWith": "繼續處理：",
  "workSettings.title": "工作流",
  "workSettings.refresh": "重新整理",
  "workSettings.initialConditions": "首次送出時的條件",
  "workSettings.submittedInstructions": "實際送出的工作指示",
  "workSettings.noHistory": "這個對話沒有保存起始條件。",
  "workSettings.unavailable": "目前無法使用這個工作流，請選擇其他工作流。",
  "workSettings.incompatible": "目前的 BimGo 版本無法開啟這份表單。請更新 BimGo，再重新整理。",
  "workSettings.changed": "你已修改設定，請重新套用，才能將新設定帶入對話。",
  "workSettings.applied": "已套用到對話。",
  "workSettings.currentRequest": "要求與說明：",
  "workSettings.submitted": "已送出",
  "workSettings.readonlyHint": "起始設定已保存。若要補充或調整需求，請直接在對話中說明。",
  "workSettings.autoNotice": "這個工作流會自動執行：Agent 修改模型時不會逐步詢問。你可以隨時按停止。",
  "workSettings.execution": "執行設定",
  "workSettings.estimatedTime": "預估需時",
  "workSettings.estimateInfo": "一般圖面的粗略參考，不是完成保證或逾時限制。實際時間依圖面複雜度、模型與工具呼叫上限而異。",
  "workSettings.toolLimit": "工具呼叫上限",
  "workSettings.toolLimitShort": "工具上限",
  "workSettings.toolLimitInvalid": "請輸入 {min}–{max} 的整數。",
  "workSettings.remove": "取消工作流",
  "workSettings.existingDraft": "對話框已有草稿",
  "workSettings.append": "附加",
  "workSettings.replace": "替換",
  "workSettings.validation.field": "此欄位",
  "workSettings.validation.value": "請確認此欄位的內容。",
  "workSettings.validation.required": "請填寫{field}。",
  "workSettings.validation.maxLength": "請縮短至 {limit} 字以內。",
  "workSettings.validation.minLength": "請至少填寫 {limit} 個字。",
  "workSettings.validation.minItems": "請至少選擇 {limit} 項。",
  "workSettings.checklist.count": "已選 {count}／{total} 項",
  "workSettings.checklist.all": "全選",
  "workSettings.checklist.none": "取消全選",
  "workSettings.checklist.choose": "選擇",
  "workSettings.validation.option": "請選擇有效的選項。",
  "workSettings.file.browse": "瀏覽檔案",
  "workSettings.file.browseFolder": "選擇資料夾",
  "workSettings.file.template": "取得空白表單",
  "workSettings.file.generate": "依模型產生填寫表",
  "workSettings.file.generating": "正在讀取模型…",
  "workSettings.file.generated": "填寫表已產生並帶入此欄位，列出 {types} 個類型。填寫完成並存檔後再開始。",
  "workSettings.file.generatedCopy": "已另存一份新的填寫表並帶入此欄位，列出 {types} 個類型，原本填的內容都保留。原檔沒有變動。",
  "workSettings.file.generateConnect": "請先連接 Revit 並開啟模型。",
  "workSettings.file.revitBusy": "Revit 正在執行對話。請等它結束或停止後再試一次。",
  "workSettings.file.revitFailed": "Revit 無法讀取模型。請確認 Revit 仍開著並已載入專案，再試一次。",
  "workSettings.file.unavailable": "目前的執行環境無法使用這個功能。",
  "workSettings.file.reason.form-not-found": "找不到這個檔案。",
  "workSettings.file.reason.not-a-form": "這個檔案不是 COBie 填寫表。請選擇填寫表，或清空欄位重新產生。",
  "workSettings.file.reason.no-types": "模型中勾選的品類沒有任何元件，沒有類型可以列出。",
  "workSettings.file.reason.file-busy": "無法寫入填寫表。若檔案正在 Excel 開啟，請先關閉再試一次。",
  "workSettings.file.templateSaved": "空白表單已存好並帶入此欄位。填寫完成並存檔後再開始。",
  "workSettings.file.templateBusy": "無法存到該位置。若同名檔案正在開啟，請先關閉再試一次。",
  "workSettings.file.failed": "未能完成，請再試一次，或直接輸入路徑。",
  "workSettings.context.indexUnavailable": "模型資料尚未更新，請按重新整理。",
  "workflows.noRevit": "請先開啟 Revit 才能使用此工作流",
  "workSettings.context.blocked": "更新模型資料",
  "workSettings.context.refreshFailed": "模型資料更新失敗，請確認 Revit 狀態後再試一次。",
  "workSettings.context.modelChanged": "模型資料已變更，請重新載入選項。",
  "workSettings.context.reselect": "模型資料已變更，請重新選取項目。",
  "workSettings.context.noItems": "目前沒有可選取的資料。",
  "workSettings.context.select": "請選取",
  "workSettings.context.agentChoice": "自動判斷",
  "workSettings.context.connect": "請先連線並選取 Revit。",
  "workSettings.context.hostChanged": "Revit 的連線或選擇已變更。請確認選取的 Revit，再重新整理並選取項目。",
  "workSettings.context.modelChangedReselect": "模型資料已變更，請重新整理並選取項目。",
  "workSettings.context.typeMismatch": "這個項目不適用於此欄位，請選擇其他項目。",
  "workSettings.context.itemUnavailable": "找不到先前選取的項目，請重新選取。",
  "settings.title": "設定",
  "settings.subtitle": "您的帳戶與此版本",
  "settings.section.user": "使用者",
  "settings.section.organization": "組織",
  "settings.section.wallet": "帳單與方案",
  "account.organizations": "組織",
  "account.signOut": "登出",

  "menu.label": "選單",
  "menu.view": "檢視",
  "menu.reload": "重新載入",
  "menu.devtools": "開發者工具",
  "menu.help": "說明",
  "menu.about": "關於",
  "menu.packageLogs": "打包日誌",
  // 回報問題（帳號選單，設定上方）
  "ticket.open": "回報問題",
  "ticket.title": "回報問題",
  "ticket.subtitle": "告訴我們發生了什麼事。附上截圖能幫助我們更快找出原因。",
  "ticket.titleLabel": "標題",
  "ticket.titlePlaceholder": "簡述問題",
  "ticket.descriptionLabel": "描述",
  "ticket.descriptionPlaceholder": "發生了什麼事？您當時在做什麼？預期的結果是什麼？",
  "ticket.screenshotLabel": "截圖（選填）",
  "ticket.screenshotHint": "png、jpg、gif 或 webp，限 1 張，不超過 {limit} MB。",
  "ticket.chooseScreenshot": "選擇截圖",
  "ticket.removeScreenshot": "移除",
  "ticket.fileError.unsupported_type": "{file} 的格式不支援，只能上傳截圖（png、jpg、gif、webp）。",
  "ticket.fileError.exceeds_size": "{file} 超過 {limit} MB 上限。",
  "ticket.fileError.empty": "{file} 是空檔案。",
  "ticket.submit": "送出",
  "ticket.submitting": "送出中…",
  "ticket.submitted": "已收到您的回報，問題編號 #{number}，感謝您的回饋！",
  "ticket.submitFailed": "回報送出失敗：{message}",
  // The caption buttons (bridge v8), drawn by the titlebar itself.
  "window.minimize": "最小化",
  "window.maximize": "最大化",
  "window.restore": "還原",
  "window.close": "關閉",

  "hosts.connectedTitle": "已連線的外部軟體",
  "connection.agent.ready": "Agent 已連線",
  "connection.agent.resolving": "正在檢查 Agent 連線…",
  "connection.agent.starting": "正在啟動 Agent…",
  "connection.agent.no-desktop": "瀏覽器無法連線至 Agent",
  "connection.agent.no-agent-channel": "此桌面版本不支援 Agent 連線",
  "connection.agent.stopped": "Agent 尚未啟動",
  "connection.agent.failed": "無法連線至 Agent",
  "connection.agent.failedDetail": "無法連線至 Agent：{detail}",
  "connection.hosts.unsupported": "無法偵測 Revit／AutoCAD",
  "connection.hosts.loading": "正在檢查 Revit／AutoCAD 連線…",
  "connection.hosts.error": "Revit／AutoCAD 連線偵測失敗",
  "connection.hosts.none": "未連線 Revit／AutoCAD",

  "hosts.none": "未連線任何外部軟體",
  "instance.lockedDuringTurn": "執行中，完成後可切換",
  "instance.totalCount": "共 {count} 個",
  "instance.connectedCount": "已連線 {count} 個",
  "instance.current": "目前使用",
  "instance.switched": "已切換至 {product} · {name}",
  "instance.autoSwitched": "原連線已關閉，已切換至 {product} · {name}",
  "about.title": "關於 Bimgo AI 2.0",
  "about.desktop": "桌面應用程式",
  "about.plugin": "已安裝擴充功能",
  "about.instances": "已連線的外部軟體",
  "about.loading": "檢查中…",
  "about.unavailable": "此桌面版本未提供",
  "about.pluginNone": "尚未安裝",
  "about.instancesNone": "尚未連線",
  "about.untitled": "未命名",
  "common.close": "關閉",

  "dashboard.title": "\u7528\u91cf\u5100\u8868\u677f",
  "dashboard.subtitle": "\u6aa2\u8996\u60a8\u672c\u6708\u7684\u7528\u91cf\u8207\u8fd1\u671f\u8acb\u6c42\u8da8\u52e2\u3002",
  "dashboard.section.personal": "\u500b\u4eba\u7528\u91cf",
  "dashboard.section.organization": "\u7d44\u7e54\u7528\u91cf",
  "dashboard.admin.members.colRemainingTokens": "Tokens \u5269\u9918",
  "dashboard.admin.members.sortLabel": "\u6210\u54e1\u6392\u5e8f",
  "dashboard.admin.members.sort.requests": "\u4f9d\u8acb\u6c42\u6392\u5e8f",
  "dashboard.admin.members.sort.tokens": "\u4f9d Tokens \u6392\u5e8f",
  "dashboard.admin.members.sort.remainingTokens": "\u4f9d Tokens \u5269\u9918\u6392\u5e8f",
  "dashboard.admin.members.sort.email": "\u4f9d Email \u6392\u5e8f",

  "plugins.title": "擴充功能",
  "plugins.subtitle": "初次安裝或重大更新需要先關閉 Revit 或 AutoCAD，再進行安裝",
  "pluginManage.title": "管理 {host} 插件",
  "pluginManage.refresh": "重新偵測",
  "pluginManage.detecting": "偵測中…",
  "pluginManage.empty.current": "偵測到的 {host} 都已是最新。",
  "pluginManage.empty.unavailable": "部分偵測到的軟體目前取得不到可安裝的插件，可能是連不上更新來源。請確認連線後重新偵測。",
  "pluginManage.empty.no-hosts": "此電腦上未偵測到 {host} 安裝。",
  "pluginManage.empty.no-candidates": "目前沒有可安裝或更新的項目；不支援的軟體版本與開發者本機版本不列入。",
  "pluginManage.kind.install": "安裝",
  "pluginManage.kind.update": "更新",
  "pluginManage.kind.downgrade": "退回",
  "pluginManage.kind.repair": "修復",
  "pluginManage.pending": "管理：{count} 個待安裝或更新",
  "pluginManage.found": "可安裝或更新({count})",
  "pluginManage.selectAll": "全選",
  "pluginManage.selected": "已選 {count} / {total}",
  "pluginManage.progress.write": "安裝中… 已完成 {done} / {total}",
  "pluginManage.progress.remove": "移除中… 已完成 {done} / {total}",
  "pluginManage.summary.done": "已完成({count})",
  "pluginManage.summary.partial": "{done} 個完成，{failed} 個失敗。",
  "pluginManage.closeFirst": "{host} {year} 開啟中，執行前請先關閉。",
  "pluginManage.run.confirm": "確認({count})",
  "pluginManage.run.remove": "移除({count})",
  "pluginManage.removeConfirm.title": "移除 {host} 插件？",
  "pluginManage.removeConfirm.body": "將從以下 {host} 版本移除插件，之後可隨時從「管理」重新安裝。",
  "pluginManage.result.queued": "等待中…",
  "pluginManage.result.running": "處理中…",
  "pluginManage.preparing": "準備中…",
  "pluginManage.result.done": "完成",
  "pluginManage.result.failed": "失敗：{reason}",
  "pluginManage.restart": "重新開啟 {host} 即可載入新的插件。",
  "plugins.cancel": "取消",
  "plugins.close": "關閉",
  "plugins.manage.title": "管理 {host} 擴充功能",
  "plugins.manage.body": "為每個偵測到的 {host} 版本安裝、修復、更新或移除擴充功能。",
  "plugins.toast.dismiss": "關閉",
  "plugins.retry": "重試",
  "plugins.checking": "正在檢查已安裝的擴充功能…",
  "plugins.unsupported": "此桌面版本無法管理擴充功能。",
  "plugins.unsupportedHint":
    "請更新桌面應用程式以從此安裝與檢查廠商軟體擴充功能。",
  "plugins.detectFailed": "無法檢查已安裝的擴充功能。",
  "plugins.status.updateAvailable": "有可升級版本",
  "plugins.status.rollbackAvailable": "有可回復版本",
  "plugins.state.updateTo": "可更新至 {version}",
  "plugins.state.installTo": "可安裝 {version}",
  "plugins.state.downgradeTo": "回復至 {version}",
  "plugins.install.target": "將安裝版本 {version}。",
  "plugins.install.fromNetwork": "將下載最新發佈的版本。",
  "plugins.install.fromApp": "使用應用程式內建的版本。",
  "plugins.status.running": "執行中",
  "plugins.status.installed": "已安裝",
  "plugins.status.attention": "需要處理",
  "plugins.status.notInstalled": "尚未安裝",
  "plugins.status.sessions": "{count} 個已開啟",
  "plugins.state.ready": "已安裝",
  "plugins.state.external": "由本機組建載入",
  "plugins.state.incomplete": "已安裝，但未向 Revit 註冊",
  "plugins.state.orphaned": "已註冊，但缺少檔案",
  "plugins.state.absent": "尚未安裝",
  "plugins.state.unsupported": "尚未支援",
  "plugins.state.openNow": "目前開啟中",
  "plugins.state.restartRequired": "固定主程式需要重新啟動 {host}",
  "plugins.card.manage": "管理",
  "plugins.card.remove": "移除",
  "plugins.card.update": "更新",
  "plugins.card.updating": "更新中…",
  "plugins.card.install": "安裝",
  "plugins.card.installing": "安裝中…",
  "plugins.card.installHint": "下載並安裝——請先關閉此版本的 {host}。",
  "plugins.card.installedLabel": "已安裝於 {host}",
  "plugins.card.repair": "修復",
  "plugins.card.repairHint": "重新安裝固定主程式與命令集——請先關閉此版本的 {host}。",
  "plugins.card.downgrade": "降級",
  "plugins.card.downgradeHint": "回復至較舊版本——請先關閉此版本的 {host}。",
  "plugins.progress.queued": "排隊中…",
  "plugins.progress.working": "處理中…",
  "plugins.progress.downloading": "下載中…",
  "plugins.progress.installing": "安裝中…",
  "plugins.progress.removing": "移除中…",
  "plugins.progress.checking": "檢查中…",
  "plugins.progress.waitingClose": "等待中…",
  // 整個插件檔案替換前，若該年份主程式開啟中先詢問（#189）。
  "plugins.closeHost.title": "請先關閉 {host} {years}",
  "plugins.closeHost.body": "安裝會替換 {host} {years} 已載入的插件檔案，因此必須先關閉。請儲存工作並關閉所有 {host} {years} 視窗，再按「繼續」。",
  "plugins.closeHost.stillOpen": "{host} {years} 仍在執行中，請關閉後再按「繼續」。",
  "plugins.closeHost.continue": "繼續",
  "plugins.closeHost.checking": "檢查中…",
  "plugins.update.hostOpen": "{host} {years} 仍開啟中，未進行安裝。請關閉後再試一次。",
  "plugins.card.updateHotHint": "即時套用 — Revit 可保持開啟。",
  "plugins.card.closeFirst": "請先關閉此 {host} 工作階段。",
  "plugins.card.restartRequired": "將更新固定主程式——請重新啟動 {host} 以完成。",
  "plugins.remove.done": "已移除。",
  "plugins.remove.failed": "無法移除擴充功能。",
  "plugins.update.done": "已更新至 {version}。請重新啟動 {host} 以載入固定主程式。",
  "plugins.update.failed": "無法更新擴充功能。",
  "plugins.update.installed": "已安裝 {version}。請重新啟動 {host} 以載入固定主程式。",
  "plugins.update.installFailed": "無法安裝擴充功能。",
  "plugins.update.downgraded": "已回復至 {version}。請重新啟動 {host} 以載入固定主程式。",
  "plugins.update.downgradeFailed": "無法回復擴充功能。",
  "plugins.update.repaired": "已修復 {version}。請重新啟動 {host} 以載入固定主程式。",
  "plugins.update.repairFailed": "無法修復擴充功能。",
  "plugins.update.reloaded": "已在 {count} 個開啟中的工作階段重新載入命令集。",
  "plugins.update.reloadIdle": "已更新，不須重啟。",
  "plugins.update.reloadPartial": "已在 {reloaded}/{requested} 個工作階段重新載入命令集。請重新啟動 {host} 以完成。",
  "plugins.update.reloadFailed": "命令集已更新至磁碟。請重新啟動 {host} 以完成。",
  "plugins.session.connected": "已連線",
  "plugins.session.notResponding": "無回應",
  "plugins.session.checking": "檢查中…",
  "plugins.host.untitled": "未命名文件",
  "plugins.catalog.comingSoon": "即將推出",
  "plugins.catalog.add": "新增 {name}",
  "plugins.catalog.revit.description":
    "在對話中直接操作 Revit 模型：讀取元件、修改參數並執行指令。",
  "plugins.catalog.autocad.description":
    "在對話中直接操作 AutoCAD 圖檔：讀取圖層、修改線稿並執行指令。",
  "plugins.install.modalTitle": "安裝至 Revit",
  "plugins.install.modalBody": "選擇要安裝擴充功能的 Revit 版本。",
  "plugins.install.action": "安裝",
  "plugins.install.patchAction": "立即更新",
  "plugins.install.installing": "安裝中…",
  "plugins.install.done": "已安裝。請重新啟動 Revit 以載入擴充功能。",
  "plugins.install.patchHint": "這是小型更新，可在 Revit 開啟的狀態下直接套用。",
  "plugins.install.patchDone": "已更新。開啟中的 Revit 已即時套用，無需重新啟動。",
  "plugins.install.noBuild": "目前取得不到可安裝的版本，可能是連不上更新來源。已安裝的版本不受影響，可以正常使用。",
  "plugins.install.noHost": "此電腦上未找到 {host} 安裝。",
  "plugins.install.unsupportedHost": "找到 {host} {found}，但目前僅支援 {supported}。",
  "plugins.install.availableYears": "目前已發布的 {host} 擴充功能版本：{years}。",

  "dashboard.summary.title": "\u672c\u9031\u671f\u7528\u91cf",
  "dashboard.summary.plan": "\u65b9\u6848",
  "dashboard.summary.requests": "\u6b21\u8acb\u6c42",
  "dashboard.summary.tokens": "Tokens",
  "dashboard.summary.remaining": "\u5269\u9918",
  "dashboard.summary.unlimited": "\u7121\u4e0a\u9650",
  "dashboard.summary.creditLimit": "組織 AI Token 個人使用",
  "dashboard.summary.resetsAt": "\u9031\u671f\u91cd\u7f6e\u65e5",
  "dashboard.summary.noPlan": "\u627e\u4e0d\u5230\u6709\u6548\u7684\u65b9\u6848\u3002",
  "dashboard.summary.loadFailed": "\u7121\u6cd5\u8f09\u5165\u7528\u91cf\u8cc7\u8a0a\uff0c\u8acb\u7a0d\u5f8c\u518d\u8a66\u3002",
  "dashboard.chart.range.7d": "近 7 天",
  "dashboard.chart.range.14d": "近 14 天",
  "dashboard.chart.range.30d": "近 30 天",
  "dashboard.chart.range.90d": "近 90 天",
  "dashboard.analytics.currentCycle": "目前計費週期",
  "dashboard.analytics.historical": "歷史使用分析",
  "dashboard.analytics.customRange": "自訂日期區間",
  "dashboard.analytics.apply": "套用",
  "dashboard.analytics.reset": "重設",
  "dashboard.analytics.requests": "請求次數",
  "dashboard.analytics.rawTokens": "Token",
  "dashboard.analytics.activeMembers": "活躍成員",
  "dashboard.analytics.activeUsers": "活躍使用者",
  "dashboard.analytics.cloudSkillsTotal": "Cloud Skills",
  "dashboard.analytics.cloudSkillsCreated": "新增 Cloud Skills",
  "dashboard.analytics.trends": "區間使用趨勢",
  "dashboard.analytics.date": "日期",
  "dashboard.analytics.empty": "此區間沒有使用活動。",
  "dashboard.analytics.loadFailed": "無法載入組織分析資料，請稍後再試。",
  "dashboard.analytics.rangeIncomplete": "請選擇完整的開始與結束日期。",
  "dashboard.analytics.rangeTooLong": "日期區間不可超過 366 天。",
  "dashboard.analytics.from": "開始日期",
  "dashboard.analytics.to": "結束日期",
  "dashboard.chart.title": "\u7528\u91cf\u8da8\u52e2",
  "dashboard.chart.requests": "\u8acb\u6c42",
  "dashboard.chart.tokens": "Tokens",
  "dashboard.chart.empty": "\u6b64\u671f\u9593\u5c1a\u7121\u7528\u91cf\u7d00\u9304\u3002",
  "dashboard.chart.loadFailed": "\u7121\u6cd5\u8f09\u5165\u8da8\u52e2\u8cc7\u6599\uff0c\u8acb\u7a0d\u5f8c\u518d\u8a66\u3002",
  "dashboard.admin.summary.title": "\u7d44\u7e54\u9031\u671f\u7528\u91cf",
  "dashboard.admin.summary.plan": "\u7d44\u7e54\u65b9\u6848",
  "dashboard.admin.summary.noPlan": "\u7121\u6709\u6548\u65b9\u6848",
  "dashboard.admin.summary.resetsAt": "\u9031\u671f\u91cd\u7f6e\u65e5",
  "dashboard.admin.summary.requests": "\u672c\u6708\u7e3d\u8acb\u6c42",
  "dashboard.admin.summary.tokens": "\u672c\u6708\u7e3d Tokens",
  "dashboard.admin.summary.activeMembers": "\u672c\u6708\u6d3b\u8e8d\u6210\u54e1",
  "dashboard.admin.summary.totalMembers": "\u7d44\u7e54\u6210\u54e1\u7e3d\u6578",
  "dashboard.admin.summary.noData": "\u5c1a\u7121\u7d44\u7e54\u7528\u91cf\u8cc7\u6599\u3002",
  "dashboard.admin.summary.loadFailed": "\u7121\u6cd5\u8f09\u5165\u7d44\u7e54\u7528\u91cf\uff0c\u8acb\u7a0d\u5f8c\u518d\u8a66\u3002",
  "dashboard.admin.chart.title": "\u7d44\u7e54\u8acb\u6c42\u8da8\u52e2",
  "dashboard.admin.members.title": "\u6210\u54e1\u7528\u91cf",
  "dashboard.admin.members.colEmail": "\u96fb\u5b50\u90f5\u4ef6",
  "dashboard.admin.members.colRequests": "\u672c\u6708\u8acb\u6c42",
  "dashboard.admin.members.colTokens": "\u672c\u6708 Tokens",
  "dashboard.admin.members.empty": "\u672c\u7d44\u7e54\u5c1a\u7121\u6210\u54e1\u3002",

  "projects.noOrganization": "尚未加入組織",
  "projects.noOrganizationDesc": "您目前沒有隸屬任何組織",
  "projects.contactAdmin": "請聯繫管理員將您加入組織",
  "projects.subscriptionExpired": "訂閱已過期",
  "projects.subscriptionExpiredDesc": "無法存取 Bimgo AI 2.0",
  "projects.subscriptionExpiredContactBimgo": "請聯繫 Bimgo 續訂或啟用組織訂閱",
  "projects.subscriptionExpiredSwitchOrg": "您可以從帳號選單切換至其他組織。",
  "projects.backToLogin": "返回登入頁",


  "wallet.loadFailed": "載入資料失敗。",
  "wallet.subscription.title": "訂閱",
  "wallet.subscription.none": "無有效訂閱。",
  "wallet.subscription.active": "使用中",
  "wallet.subscription.plan": "方案名稱",
  "wallet.subscription.renewsOn": "續訂日",
  "wallet.subscription.period": "訂閱期間",
  "wallet.subscription.daysLeft": "剩餘 {count} 天",
  "wallet.plan.title": "方案",
  "wallet.plan.empty": "尚未綁定方案。",
  "wallet.plan.maxCredits": "每月方案額度 AI Token 上限",
  "wallet.plan.cycleReset": "重置日",
  "wallet.balance.title": "Bimgo AI Token 餘額",
  "wallet.balance.expiringSoon": "有 {credits} AI Token 即將到期。",
  "wallet.balance.nextExpiry": "最近到期日：{date}。",
  "wallet.purchases.title": "購買與儲值紀錄",
  "wallet.purchases.colDate": "日期",
  "wallet.purchases.colType": "類型",
  "wallet.purchases.colCredits": "點數",
  "wallet.purchases.colBalance": "餘額",
  "wallet.purchases.colExpires": "到期日",
  "wallet.purchases.colReference": "備註",
  "wallet.purchases.empty": "尚無購買或儲值紀錄。",

  "limits.save": "儲存",
  "limits.saving": "儲存中…",
  "limits.cancel": "取消",
  "limits.edit": "編輯",
  "limits.unlimited": "無上限",
  "limits.invalid": "請輸入 0 或以上的數字，或留空。",
  "limits.saveFailed": "儲存失敗，請稍後再試。",
  "limits.plan.mode": "方案模式",
  "limits.plan.seats": "用戶席次",
  "limits.mode.per_seat": "席次方案",
  "limits.mode.org_shared": "企業方案",
  "limits.org.title": "每月組織 AI Token 上限",
  "limits.org.editTitle": "設定每月組織 AI Token 上限",
  "limits.org.hint": "限制整個組織每月可使用的組織 AI Token。席次方案已付費的個人額度不計入此上限。",
  "limits.org.default": "預設值",
  "limits.org.placeholder": "例：75000000",
  "limits.org.clearHint": "留空即恢復預設值（座位數 × 方案額度）。",
  "limits.members.title": "成員組織 AI Token 使用",
  "limits.members.editTitle": "設定成員每月 Bimgo AI Token 上限",
  "limits.members.hint": "每位成員每月最多可從組織錢包扣抵的額度。",
  "limits.members.hintPerSeat": "席次方案：先扣完個人額度，超出的部分才從錢包扣抵。",
  "limits.members.hintOrgShared": "企業方案：沒有個人額度，每次請求都直接從錢包扣抵。",
  "limits.members.blankPerSeat": "留空等同 0：在您設定上限前，不會扣抵組織錢包。",
  "limits.members.blankOrgShared": "留空代表不設個人上限，僅受組織上限與錢包餘額限制。",
  "limits.members.colMember": "成員",
  "limits.members.colCap": "個人上限",
  "limits.members.colSpent": "個人已使用",
  "limits.members.exhausted": "已達上限",
  "limits.members.inherit": "留空：使用預設值",
  "limits.members.empty": "此組織尚無成員。",
  "limits.members.applyAll": "套用至全組織成員：",
  "limits.members.applyAllPlaceholder": "例：15000000",
  "limits.members.applyAllAction": "全部套用",

  "error.validationError": "請求參數有誤，請檢查後重試。",
  "error.unauthorized": "登入已過期，請重新登入。",
  "error.forbidden": "您沒有權限執行此操作。",
  "error.notFound": "找不到請求的資源，請重試。",
  "error.instanceBusy": "{instance} 正在處理另一個對話。請等該對話完成或停止後再試。",
  "error.instanceBusyUnknown": "所選的軟體正在處理另一個對話。請等該對話完成或停止後再試。",
  "error.threadBusy": "此對話仍在回覆中。請等回覆完成或停止後再傳送。",
  "threadStatus.unavailable": "此對話不適用於目前的帳號或伺服器。",
  "threadStatus.ownerMismatch": "此對話屬於其他工作區，無法在此繼續。",
  "threadStatus.noLocalWorkspace": "此對話在這台裝置上沒有工作區，因此無法在此繼續，但仍可閱讀。",
  "threadStatus.loadFailed": "無法載入此對話。",
  "threadStatus.startNew": "開始新對話",
  "error.runTargetChanged": "這次執行原本是在「{document}」中進行的。請在 {host} 開啟並選取該模型，再按一次「繼續執行」。若要改在目前選取的模型中繼續，請直接送出新的訊息。",
  "error.runTargetChangedUnknown": "這次執行原本是在另一個模型中進行的。請在 {host} 開啟並選取該模型，再按一次「繼續執行」。若要改在目前選取的模型中繼續，請直接送出新的訊息。",
  "error.runTargetUnknown": "找不到這次執行原本所在模型的紀錄，無法繼續執行。請送出新的訊息來接續工作。",
  "error.usageLimitExceeded": "此請求被組織的帳務限制擋下。請至「設定 › 帳單與方案」查看，或聯繫組織管理員。",
  "error.subscriptionRequired": "您的組織沒有有效訂閱，請聯繫 Bimgo 續訂或啟用。",
  "error.insufficientCredits": "組織的 Bimgo AI Token 已用盡，可由組織管理員儲值。",
  "error.memberLimitExceeded": "您的個人額度已用盡，且在此組織沒有可用的加購額度，請組織管理員授予或調高。",
  "error.orgLimitExceeded": "組織已達本月用量上限，可由組織管理員調高。",
  "error.contextLengthExceeded": "對話內容過長，請開啟新對話後重試。",
  "error.pluginUpdateRequired": "請前往「擴充功能」更新 Revit 擴充功能，重新啟動 Revit 後再試。",
  "error.agentRecursionLimit": "AI 處理步驟已達上限，請簡化您的問題或重新嘗試。",
  "error.agentToolError": "AI 工具執行失敗，請重新嘗試。",
  "error.agentParseError": "AI 回應解析失敗，請重新嘗試。",
  "error.modelAuthenticationError": "AI 模型驗證失敗，請聯繫管理員。",
  "error.modelNotFound": "找不到指定的 AI 模型，請聯繫管理員。",
  "error.modelRateLimit": "AI 請求過於頻繁，請稍後再試。",
  // See the English side for why this is separate from the rate limit (#129).
  "error.modelOverloaded": "{provider} 目前忙碌中，請稍候再試一次。",
  "error.modelOverloadedUnknown": "AI 服務目前忙碌中，請稍候再試一次。",
  "error.modelConnectionLost": "{provider}：回應途中連線中斷，請再試一次。",
  "error.modelConnectionLostUnknown": "與 AI 服務的連線在回應途中中斷，請再試一次。",
  "error.modelAborted": "AI 處理已中斷，請重新嘗試。",
  "error.upstreamError": "服務暫時異常，請稍後再試。",
  "error.serviceUnavailable": "服務目前無法使用，請稍後再試。",
  "error.internalError": "系統發生錯誤，請稍後再試。",
  "error.unknown": "發生未預期的錯誤，請稍後再試。",

  "hitl.approvalRequired": "需要確認",
  "hitl.approved": "已確認",
  "hitl.rejected": "已拒絕",
  "hitl.revised": "已送出修改",
  "hitl.answered": "已回覆",
  "hitl.yourAnswer": "你的回答：",
  "hitl.askUserTitle": "需要你的確認",
  "hitl.askUserOtherPlaceholder": "其他（自行輸入答案）",
  "hitl.askUserSubmit": "送出回答",
  "hitl.askUserCancel": "取消",
  "hitl.askUserCancelHint": "停止目前任務，保留先前已完成的操作",
  "hitl.generateCode": "生成程式碼",
  "hitl.executeSkill": "執行技能",
  "hitl.saveSkill": "儲存技能",
  "hitl.planReview": "審查計畫",
  "hitl.planLabel": "計畫",
  "hitl.editPlan": "編輯計畫",
  "hitl.planFeedbackPlaceholder": "想怎麼調整計畫？可直接編輯上方計畫或在此留言",
  "hitl.requestChanges": "送出修改",
  "hitl.execute": "執行",
  "hitl.approve": "確認執行",
  "hitl.reject": "拒絕",
  "hitl.approveBlockedByComment":
    "你有填寫意見。請改用「送出修改」，或清除意見後再核可。",
  "activePlan.label": "執行中的計畫",
  "activePlan.dismiss": "關閉計畫",
  "activePlan.close": "關閉",
  "activePlan.updated": "計畫已更新，點擊查看",
  "activePlan.download": "下載計畫 (.md)",
  "activePlan.copy": "複製計畫",
  "activePlan.copied": "已複製",

  "starterPrompts.sectionLabel": "專案化提示範例",
  "starterPrompts.cta": "開始與 Bimgo AI 協作",
  "starterPrompts.ctaPrefix": "開始與",
  "starterPrompts.ctaSuffix": "協作",
  "skills.vendorLabel": "應用程式",
  "skills.vendorComingSoon": "即將推出",
  "skills.versionScopeLabel": "顯示下方所選 Revit 版本的技能",
  "skills.versionOpen": "已開啟",
  "skills.sortLabel": "排序方式",
  "skills.sort.newest": "最新優先",
  "skills.sort.oldest": "最舊優先",
  "pager.label": "清單分頁",
  "pager.prev": "上一頁",
  "pager.next": "下一頁",
  "pager.pagesSkills": "第 {page} / {pageCount} 頁 - 共 {total} 個技能",
  "pager.pagesItems": "第 {page} / {pageCount} 頁 - 共 {total} 個項目",
  "pager.totalSkills": "共 {total} 個技能",
  "pager.totalItems": "共 {total} 個項目",
  "skills.pageSize": "每頁顯示",
  "skills.pageSizeOption": "{count} 筆 / 頁",
  "starterPrompts.openModelFallback": "請先在 Revit 開啟 BIM 模型，即可取得依你專案內容產生的提示。以下為通用範例。",
  "starterPrompts.genericFallback": "無法讀取已開啟的 BIM 模型，以下提供通用範例。",
  "starterPrompts.loading": "正在了解已開啟的 BIM 模型…",
  "starterPrompts.usageFallback": "AI 額度不足，以下範例改由已開啟的 BIM 模型直接產生。",
  "starterPrompts.contextFallback": "AI 目前無法產生範例，以下範例改由已開啟的 BIM 模型直接產生。",
  "settings.user.timeout.title": "Revit 任務時限",
  "settings.user.timeout.description": "AI 執行單一 Revit 任務的等待上限。任務較複雜（例如大量建模）時可調長。",
  "settings.user.timeout.seconds": "{seconds} 秒",
  "settings.user.timeout.default": "預設",
  "settings.user.timeout.loading": "載入中…",
  "settings.user.timeout.unavailable": "無法連線到本機代理程式，暫時無法變更。",
  "settings.user.timeout.savedTitle": "已更新任務時限",
  "settings.user.timeout.savedDesc": "下一個任務即生效，不需重啟 Revit。",
  "settings.user.timeout.saveErrorTitle": "更新任務時限失敗",
  "message.saveSkill": "儲存 Skill",
  "toast.toolSaveSuccess": "已儲存技能",
  "toast.toolSaveError": "儲存技能時發生錯誤",
  "skills.saveNeedsYear": "這段程式碼沒有在已知的 Revit 版本上執行，因此無法儲存為技能。",
  "starterPrompts.generic.overview": "說明目前模型的樓層與空間組成",
  "starterPrompts.generic.count": "依類別統計目前模型中的元素數量",
  "starterPrompts.generic.export": "將目前模型的基本資訊整理成 CSV 報表",

  "messageInput.modeLabel": "模式",
  "messageInput.mode.hitl": "Review",
  "messageInput.mode.auto": "自動",
  "messageInput.mode.plan": "規劃",
  "messageInput.modeDesc.hitl": "執行重要步驟前先向你確認",
  "messageInput.modeDesc.auto": "不經逐步確認，自動完成任務",
  "messageInput.modeDesc.plan": "先提出完整計畫，核准後再執行",
  "messageInput.modelLabel": "模型",
  "messageInput.reasoningLabel": "推理",
  "messageInput.reasoningReset": "還原推理程度",
  "messageInput.reasoning.none": "無",
  "messageInput.reasoning.low": "低",
  "messageInput.reasoning.medium": "中",
  "messageInput.reasoning.high": "高",
  "messageInput.reasoning.xhigh": "特高",
  "messageInput.reasoning.max": "最高",
  "messageInput.reasoningDesc.none": "不額外思考直接回答，最快，適合簡單查詢",
  "messageInput.reasoningDesc.low": "簡短思考，適合例行任務的快速回答",
  "messageInput.reasoningDesc.medium": "均衡思考，大多數工作的預設",
  "messageInput.reasoningDesc.high": "深入思考，較慢且成本較高，適合複雜建模",
  "messageInput.reasoningDesc.xhigh": "長時間思考，僅最新模型支援，其他模型以「高」執行",
  "messageInput.reasoningDesc.max": "不限時間思考，僅最新模型支援，其他模型以「高」執行",
  "messageInput.reasoningHint": "推理程度越高，回應越完整，但花費時間越長，額度也用得越快。",
  "messageInput.attach": "附加圖片、PDF、試算表或 DWG",
  "messageInput.removeAttachment": "移除附件",
  "messageInput.attachment": "附件",
  "message.toolUseTool": "使用工具",
  "mention.params.title": "{name} 的參數",
  "mention.params.loading": "載入參數中...",
  "mention.params.empty": "此元件沒有可選參數",
  "mention.params.instanceTitle": "元件參數",
  "mention.params.typeTitle": "類型參數",
  "mention.params.familyTitle": "族群參數",
  "mention.params.cadLayerTitle": "CAD 圖層",
  "mention.params.cadBlockTitle": "CAD 圖塊",
  "mention.params.clearAll": "清除",
  "mention.params.searchPlaceholder": "搜尋參數...",
  "mention.params.searchEmpty": "沒有符合的參數",
  "csv.title": "CSV 檔案",
  "csv.loading": "載入 CSV 資料中...",
  "csv.loadFailed": "載入 CSV 失敗：{error}",
  "csv.downloadFile": "下載檔案",
  "csv.download": "下載",
  "csv.showPreview": "顯示預覽",
  "csv.showFullTable": "顯示完整表格",
  "csv.showingRows": "顯示 {shown} / {total} 筆資料。",
  "csv.viewAllRows": "查看所有資料列",
  "csv.dimensions": "{rows} 列 × {cols} 欄",
  "csv.empty": "CSV 中沒有資料",

  "messageInput.placeholder": "輸入訊息",
  "mention.noResults": "找不到結果",
  "revitIndex.title": "Revit 資料庫",
  "revitIndex.open": "開啟 Revit 資料庫",
  "revitIndex.close": "關閉 Revit 資料庫",
  "revitIndex.back": "返回類別",
  "revitIndex.refresh": "重新整理索引",
  "revitIndex.searchPlaceholder": "搜尋 Revit 元件...",
  "revitIndex.loading": "載入索引中...",
  "revitIndex.missing": "尚未建立索引",
  "revitIndex.missingDescription": "建立索引後即可瀏覽並插入 Revit 元件。",
  "revitIndex.build": "建立索引",
  "revitIndex.retry": "重試",
  "revitIndex.building": "正在建立 Revit 資料庫...",
  "revitIndex.failed": "索引建立失敗",
  "revitIndex.failedDescription": "無法建立 Revit 資料庫，請重試。",
  "revitIndex.unavailable": "Revit 資料庫無法使用",
  "revitIndex.unavailableDescription": "已連線的 Revit 尚未開啟文件。",
  "revitIndex.staleWarning": "模型已變更，請重新整理以更新 Revit 資訊。",
  "revitIndex.noResults": "找不到符合的元件",
  "revitIndex.updated": "更新時間：{time}",
  "revitIndex.dragHint": "點擊插入，或拖曳到訊息輸入框",
  "revitIndex.dropToInsert": "放開以插入 Revit 物件",
  "projects.currentProject": "目前專案：{name}",
  "chat.welcomeMessage": "你好，我是你的 AEC AI 專家。今天我能為你做些什麼？",
  "chat.welcomeTitle": "你好，我是你的 AEC AI 專家。",
  "chat.welcomeSubtitle": "今天我能為你做些什麼？",
  "chat.pluginSetup.title": "連接你的 AEC 軟體",
  "chat.pluginSetup.description": "安裝 Bimgo AI 插件，即可透過 AI 操作 Revit 或 AutoCAD。",
  "chat.pluginSetup.revit": "設定 Revit",
  "chat.pluginSetup.autocad": "設定 AutoCAD",
  "chat.pluginSetup.configured": "已設定 {host}",
  "chat.pluginSetup.checking": "檢查中…",
  "chat.pluginLaunch.title": "啟動 {host}",
  "chat.pluginUpdate.title": "{host} 插件有可用更新",
  "chat.pluginUpdate.rollbackTitle": "{host} 插件有可回復版本",
  "chat.pluginUpdate.description": "請立即更新插件，確保功能可以正常運作。",
  "chat.pluginUpdate.action": "立即更新",
  "chat.pluginUpdate.actionLabel": "立即更新 {host} 插件",
  "chat.pluginLaunch.description": "啟動後 Bimgo AI 將自動連線，並開始協作。",
  "chat.pluginLaunch.eitherHost": "Revit 或 AutoCAD",
  "chat.pluginLaunch.waiting": "等待軟體連線中",
  "chat.send": "傳送",
  "chat.stop": "停止",
  "chat.stoppedNotice": "您已停止這則回覆。",
  "chat.thinking": "處理中…",
  "chat.copyMessage": "複製訊息",
  "chat.copied": "已複製",
  "chat.error.reference": "參考代碼",
  "chat.error.copyReference": "複製參考代碼",
  "chat.signInFirst": "請先登入並選擇專案以開始對話。",
  "chat.instanceInUse": "{host} 正在處理另一個對話。請等該對話完成或停止後，再於此傳送。",
  "chat.showReasoning": "顯示推理過程",
  "chat.hideReasoning": "隱藏推理過程",
  "message.showSourceCode": "顯示原始碼",
  "message.copyCode": "複製程式碼",
  "message.codeCopied": "已複製",
  "common.failed": "失敗",
  "message.toolDidNotWork": "未成功",
  "cad.changes": "比對差異",
  "cad.kind.added": "新增",
  "cad.kind.deleted": "刪除",
  "cad.kind.moved": "移動",
  "cad.kind.text": "文字",
  "cad.kind.mixed": "多處變更",
  "cad.kind.modified": "修改",
  "cad.annotations.completed": "標記已完成",
  "cad.annotations.incomplete": "標記尚未完成",
  "cad.annotations.not_requested": "僅回報差異，未要求標記",
  "cad.zooming": "正在移動畫面…",
  "cad.zoomFailed": "沒能把 AutoCAD 移到這一處。等 AutoCAD 空下來再點一次。",
  "cad.zoomWrongDrawing": "請回到這次比對的原圖，或重新比對。",
  "common.stopped": "已停止",
  "common.calling": "呼叫",
  "common.thinking": "思考中...",
  "message.toolGenerateCode": "生成程式碼",
  "message.toolLookUpDocs": "查閱 Revit 文件",
  "message.toolFilterElements": "篩選元素",
  "message.toolInspectModel": "以程式碼檢視模型",
  "message.toolGetCurrentView": "取得目前視圖",
  "message.toolGetSelectedElements": "取得選取的元素",
  "message.toolGetFamilyTypes": "列出族群類型",
  "message.toolHighlightElements": "標示元素",
  "message.toolColorSplash": "元素上色",
  "message.toolCreateSectionBox": "建立剖面框",
  "message.toolRoomCollision": "房間碰撞分析",
  "message.toolRoadwayClearance": "檢查道路淨空",
  "message.toolWallTopCapping": "檢查牆頂封頂",
  "message.toolPlaceholderFamilies": "準備佔位族",
  "message.toolFindRoadwayFloor": "尋找道路樓板",
  "message.toolDraftCode": "草擬程式碼",
  "message.toolLoadSkill": "載入技能",
  "message.toolSaveSkill": "儲存技能",
  "message.toolReadLibrary": "讀取技能庫",
  "message.toolSubmitPlan": "提交計畫",
  "message.toolAskUser": "提出問題",
  "chat.contextMeterLabel": "上下文用量",
  "chat.contextMeterTooltip": "此對話已使用 {percent}% 的模型上下文。",
  "chat.contextMeterCriticalTooltip":
    "此對話已使用 {percent}% 的模型上下文。建議開啟新對話繼續，否則下一則訊息可能無法送出。",

  "common.history": "歷史紀錄",
  "workflow.toolsBar": "工作流工具",
  "workflows.title": "工作流",
  "workflows.filter.label": "類別",
  "workflows.filter.all": "所有類別",
  "workflows.sort.label": "排序",
  "workflows.sort.category": "類別",
  "workflows.sort.available": "可用的優先",
  "workflows.sort.name": "名稱",
  "workflows.empty": "沒有符合的工作流。",
  "workflows.new": "新增工作流",
  "workflows.comingSoon": "即將推出",
  "workflows.start": "開始",
  "workflows.running": "執行中…",
  "workflows.starting": "正在開始執行…",
  "workflows.continue.stopped": "這次執行在完成前中斷了。",
  "workflows.continue.action": "繼續執行",
  "workflows.continue.destructive": "{tools} 可能已經改動了模型或檔案。請先確認結果再繼續，或改為送出新的訊息。",
  "workflows.continue.confirm": "仍要繼續",
  "workflows.continue.stoppedAt": "停在：{step}",
  "workflows.continue.stoppedThinking": "停在 Agent 思考下一步的時候。",
  "workflows.category.interoperability": "互通性",
  "workflows.category.modeling": "建模",
  "workflows.category.domain": "領域專家",
  "workflows.unsupported": "請更新桌面版以使用工作流。",
  "workflows.unavailable": "無法從 agent 讀取工作流清單。",
  "workflows.notReady.hostNotConnected": "請先連線 {host} 再開始。",
  "workflows.notOpen": "此桌面版本沒有開放這個工作流，無法在這裡繼續這個對話。",
  "chat.loading": "載入中...",
  "chat.noThreads": "尚無對話記錄",
  "chat.newConversation": "新對話",
  "chat.threadNotFound": "找不到此對話，或您沒有存取權限",
  "chat.projectAccessLost":
    "您已失去此專案的存取權限，請聯繫專案管理員",

  "projects.title": "專案",
  "projects.projectList": "專案列表",
  "projects.projectCount": "共 {count} 個專案",
  "projects.searchPlaceholder": "依名稱、描述或備註搜尋專案...",
  "projects.loadingProjects": "載入專案中...",
  "projects.loadProjectsFailed": "載入專案失敗",
  "projects.retry": "重試",
  "projects.noMatchingProjects": "沒有找到符合條件的專案",
  "projects.adjustFilter": "請嘗試調整搜尋條件或篩選器",
  "projects.noProjects": "尚未建立專案",
  "projects.createFirstProject": "開始建立您的第一個建築專案",
  "projects.noProjectsAssigned": "您尚未被指派任何專案",
  "projects.contactProjectAdmin": "請聯繫專案管理員或組織管理員將您加入專案",
  "projects.columnName": "專案名稱",
  "projects.columnDescription": "描述",
  "projects.columnStatus": "狀態",
  "projects.columnCreatedAt": "建立時間",
  "projects.columnActions": "操作",
  "projects.statusActive": "啟用",
  "projects.statusInactive": "停用",
  "projects.notes": "備註: {notes}",
  "projects.noDescription": "無描述",
  "projects.edit": "編輯",
  "projects.delete": "刪除",
  "projects.members": "成員",

  "projects.createProject": "建立專案",
  "projects.editProject": "編輯專案",
  "projects.createProjectDesc":
    "請填寫專案資訊以建立新的建築專案。專案名稱為必填項目。",
  "projects.editProjectDesc": "請修改專案資訊。專案名稱為必填項目。",
  "projects.projectName": "專案名稱 *",
  "projects.projectNamePlaceholder": "請輸入專案名稱",
  "projects.projectNameRequired": "專案名稱為必填項目",
  "projects.projectDescription": "專案描述",
  "projects.projectDescriptionPlaceholder": "請輸入專案描述（可選）",
  "projects.projectDescriptionHint": "簡要描述專案的內容和目標",
  "projects.projectNotes": "備註",
  "projects.projectNotesPlaceholder": "請輸入備註（可選）",
  "projects.projectNotesHint": "其他需要記錄的專案相關資訊",
  "projects.cancel": "取消",
  "projects.creating": "建立中...",
  "projects.updating": "更新中...",
  "projects.updateProject": "更新專案",

  "projects.confirmDelete": "確認刪除專案",
  "projects.deleteWarning": "此操作無法復原，請確認您要刪除此專案。",
  "projects.projectNameLabel": "專案名稱：",
  "projects.descriptionLabel": "描述：",
  "projects.confirmDeleteBtn": "確認刪除",

  "projects.noOrg": "未選擇組織。",
  "projects.selected": "已選擇",
  "projects.openChat": "開啟此專案的對話",
  "projects.noneSelected": "尚未選擇專案 — 請在「專案」中選擇",

  "projectMembers.title": "管理專案成員",
  "projectMembers.assignTitle": "指派成員",
  "projectMembers.searchMember": "搜尋成員...",
  "projectMembers.noMemberFound": "找不到符合的成員",
  "projectMembers.allAssigned": "所有組織成員皆已指派",
  "projectMembers.add": "指派",
  "projectMembers.empty": "尚未指派任何成員",
  "projectMembers.loadFailed": "載入專案成員失敗",
  "projectMembers.addFailed": "指派成員失敗",
  "projectMembers.selectAll": "全選",
  "projectMembers.selectMember": "選取成員",
  "projectMembers.selectedCount": "已選取",
  "projectMembers.clearSelection": "清除選取",
  "projectMembers.removeSelected": "移除",
  "projectMembers.removeConfirmTitle": "移除所選成員",
  "projectMembers.removeConfirmDescPrefix": "確定要移除所選的",
  "projectMembers.removeConfirmDescSuffix":
    "位成員嗎？他們將無法再存取此專案（組織管理員與專案建立者除外）。",
  "projectMembers.bulkRemoveFailed": "批次移除成員失敗",
  "projectMembers.removeOrgAdminBlocked":
    "組織管理員的專案存取權為隱含授予，無法在此移除",
  "projectMembers.implicitAccessNote":
    "組織管理員擁有所有專案的存取權，因此無法在此編輯其指派；專案建立者永遠擁有存取權，可能不會出現在此清單中。",

  "settings.pane.account": "帳戶",
  "settings.pane.email": "電子郵件",
  "settings.pane.organization": "組織",
  "settings.pane.about": "關於",
  "settings.pane.desktop": "桌面版本",
  "settings.pane.bridge": "橋接",
  "settings.pane.environment": "環境",
  "settings.pane.platform": "平台",
  "settings.pane.noBridge": "無桌面橋接 — 無法取得版本資訊。",
  "settings.pane.notSignedIn": "尚未登入",

  // The grace-period banner (#75) — the first half of the message whose second
  // half is `login.signedOutElsewhere`. Wording verbatim from `bimgo-ai-web`.
  "auth.signingOutSoon": "您的帳號已在其他裝置登入，此裝置將於數分鐘後自動登出。",
  "login.signedOutElsewhere":
    "您的帳號已在另一部裝置登入，因此此裝置已登出。一個帳號一次只能在一部裝置使用。",
  // A revocation nothing explains (#124) — see the English side for why.
  "login.sessionEnded": "您的登入工作階段已結束，請重新登入。",
  "login.email": "電子郵件",
  "login.emailPlaceholder": "請輸入您的電子郵件地址",
  "login.password": "密碼",
  "login.passwordPlaceholder": "請輸入您的密碼",
  "login.rememberMe": "記住我",
  "login.signIn": "登入",
  "login.signingIn": "登入中…",
  "login.forgotPassword": "忘記密碼？",
  "login.backToSignIn": "返回登入",
  "login.resetTitle": "重設您的密碼",
  "login.resetSubtitle": "我們會將設定新密碼的連結寄到您的電子郵件。",
  "login.sendResetLink": "傳送重設連結",
  "login.sending": "傳送中…",
  "login.resetSent": "若該電子郵件有對應的帳號，重設連結即將寄出，並會在您的瀏覽器中開啟。",
  "updatePassword.subtitle": "請為您的 Bimgo AI 帳號設定新密碼。",
  "updatePassword.newLabel": "新密碼",
  "updatePassword.confirmLabel": "確認新密碼",
  "updatePassword.confirmPlaceholder": "請再輸入一次",
  "updatePassword.mismatch": "兩次輸入的密碼不一致。",
  "updatePassword.checking": "正在驗證您的連結…",
  "updatePassword.accountReadyTitle": "帳號設定成功",
  "updatePassword.accountReadyDesc": "您的密碼已成功設定。",
  "updatePassword.openDesktop": "開啟 Bimgo AI 2.0",
  "updatePassword.doneReturnToApp": "您可以關閉此分頁，並在 Bimgo AI 桌面應用程式中使用新密碼登入。",
  "updatePassword.newUserTitle": "第一次使用 Bimgo AI？",
  "updatePassword.newUserDesc": "請安裝 Bimgo AI 桌面應用程式開始使用，安裝說明請見我們的文件。",
  "updatePassword.installDesktop": "安裝說明",
  "updatePassword.signOutError": "密碼已設定，但無法安全結束目前工作階段。請關閉此頁面後再試一次。",
  "updatePassword.invalidTitle": "此連結無法使用",
  "updatePassword.invalidDesc": "重設連結已過期或已被使用。",
  "updatePassword.invalidRetry": "請至 Bimgo AI 桌面應用程式的登入頁重新申請。",
  "updatePassword.title": "更新密碼",
  "updatePassword.placeholder": "輸入新密碼（至少 6 個字元）",
  "updatePassword.updating": "更新中…",
  "updatePassword.confirm": "確認",
  "updatePassword.errorTitle": "更新密碼時發生錯誤",
  "updatePassword.successDesc": "您的密碼已更新，將於下次登入時生效。",
  "updatePassword.unconfigured": "更新密碼需要已設定的 Supabase 專案。",
  "authConfirm.resetTitle": "重設您的密碼",
  "authConfirm.desc": "請點擊下方按鈕，繼續設定您的密碼。",
  "authConfirm.continue": "繼續",

  "settings.user.roles.none": "目前無角色。",
  "settings.user.roles.viewPermissions": "查看權限",

  "settings.user.language.title": "語言",
  "settings.user.language.en": "English",
  "settings.user.language.zh": "中文",
  "settings.user.language.restartNote":
    "應用程式會立即切換。桌面應用程式本身的提示將於重新啟動後變更語言。",

  "settings.diagnostics.packaged": "已打包日誌",
  "settings.diagnostics.packagedBody": "已收集 {count} 個日誌檔案 → {path}",
  "settings.diagnostics.contains":
    "此 zip 包含最近 7 天的所有本機 BimGo 日誌檔案、安裝與工作階段識別碼，以及應用程式版本；不會包含您的文件、憑證或本機資料庫。請將此檔案傳送給 BIMGO 技術支援。",
  "settings.diagnostics.skipped": "{count} 個檔案使用中，已略過。",
  "settings.diagnostics.failed": "打包日誌失敗：{error}",

  "settings.user.actions.title": "您的權限",
  "settings.user.actions.none": "目前無權限。",
  "settings.user.actions.feature.org": "組織",
  "settings.user.actions.feature.project": "專案",
  "settings.user.actions.feature.skill": "技能",
  "settings.user.actions.feature.chat": "對話",
  "settings.user.actions.action.org.read": "檢視完整資訊",
  "settings.user.actions.action.org.update": "更新",
  "settings.user.actions.action.org.manage_members": "管理成員",
  "settings.user.actions.action.org.manage_roles": "管理角色",
  "settings.user.actions.action.org.manage_secrets": "管理金鑰",
  "settings.user.actions.action.org.read_secret": "檢視",
  "settings.user.actions.action.org.read_members": "檢視成員",
  "settings.user.actions.action.project.create": "建立",
  "settings.user.actions.action.project.read": "檢視",
  "settings.user.actions.action.project.update": "更新",
  "settings.user.actions.action.project.delete": "刪除",
  "settings.user.actions.action.project.manage_members": "管理成員",
  "settings.user.actions.action.skill.create": "建立",
  "settings.user.actions.action.skill.read": "檢視",
  "settings.user.actions.action.skill.edit": "編輯",
  "settings.user.actions.action.skill.delete": "刪除",
  "settings.user.actions.action.skill.publish": "發佈",
  "settings.user.actions.action.skill.download": "下載",
  "settings.user.actions.action.skill.run": "執行",
  "settings.user.actions.action.skill.enable": "啟用",
  "settings.user.actions.action.chat.ask": "提問",

  "settings.org.members.title": "組織成員",
  "settings.org.members.colMember": "成員",
  "settings.org.members.colRoles": "角色",
  "settings.org.members.colActions": "操作",
  "settings.org.members.edit": "編輯",
  "settings.org.members.view": "查看權限",
  "settings.org.members.empty": "本組織尚無成員。",
  "settings.org.members.loadFailed": "無法載入成員列表，請稍後再試。",
  "settings.org.members.searchPlaceholder": "搜尋 Email 或角色…",
  "settings.org.members.editDialog.title": "編輯成員角色",
  "settings.org.members.editDialog.save": "儲存",
  "settings.org.members.editDialog.saving": "儲存中…",
  "settings.org.members.editDialog.cancel": "取消",
  "settings.org.members.editDialog.successTitle": "角色已更新",
  "settings.org.members.editDialog.errorTitle": "更新失敗",

  "settings.org.members.role.organization_admin": "組織管理員",
  "settings.org.members.role.project_admin": "專案管理員",
  "settings.org.members.role.advance_user": "進階使用者",
  "settings.org.members.role.standard_user": "標準使用者",
  "settings.org.members.roleDesc.organization_admin":
    "完整組織管理權限：管理成員、角色、帳務，以及所有專案與技能操作",
  "settings.org.members.roleDesc.project_admin": "管理專案（建立、更新、刪除）及所有技能操作",
  "settings.org.members.roleDesc.advance_user": "可建立、編輯、刪除及發佈技能，加上標準使用者權限",
  "settings.org.members.roleDesc.standard_user": "基本使用權限：對話、檢視專案、下載與執行技能",
  // Skills pane
  "skills.title": "技能",
  "skills.subtitle": "這台電腦上已儲存的技能，依軟體廠商與版本分組。執行技能需要連線該版本的軟體。",
  "skills.refresh": "重新整理",
  "skills.agentUnavailable": "無法連線本機代理程式，因此無法讀取技能庫。",
  "skills.fetchError": "取得技能時發生錯誤",
  "skills.pluginOutdated": "代理程式版本過舊",
  "skills.pluginOutdatedDesc": "此桌面版的代理程式不支援技能功能，請更新至最新版本後重試。",
  "skills.noInstance": "請先開啟設計軟體才能使用技能",
  "skills.vendors.label": "軟體",
  "skills.autocad.newFolderDesc": "在這台電腦為 AutoCAD 技能建立技能資料夾。",
  "skills.autocad.years": "支援 AutoCAD",
  "skills.crossVersion.prompt": "請將此技能建置到其他 AutoCAD 版本。",
  "skills.crossVersion.unsupported": "此技能不支援 AutoCAD {version}。請在標題列選取支援的 AutoCAD 以執行或編輯。",
  "skills.crossVersion.unsupportedChip": "不支援 AutoCAD {version}",
  "skills.crossVersion.buildOthers": "建置到其他版本",
  "instance.unsupportedBySkill": "目前開啟的技能不支援 AutoCAD {version}。請先從支援的版本將它建置到 {version}。",
  "skills.crossVersion.buildOthersHint": "代理會詢問要加入哪些 AutoCAD 版本，在已開啟的版本上檢查程式碼，並在儲存前請您確認。",
  "skills.crossVersion.chatBlocked": "AutoCAD {version} 無法使用。請在標題列選取支援的 AutoCAD。",
  "skills.versionGone.title": "此版本不支援 AutoCAD {year}",
  "skills.versionGone.desc": "v{version} 僅支援 AutoCAD {versions}，無法在目前連線的 AutoCAD {year} 執行或編輯。",
  "skills.versionGone.back": "返回技能列表",
  "skills.noInstanceDesc": "技能會依軟體與版本分別儲存，例如 Revit 或 AutoCAD。",
  "skills.noInstanceHint": "開啟您使用的軟體，並在標題列選取它，即可顯示相關技能。",
  "skills.noSkillsForVersion": "此版本尚無技能。請在對話中針對此軟體儲存一個。",
  "skills.searchPlaceholder": "依名稱或描述搜尋技能",
  "skills.noMatch": "沒有符合搜尋條件的技能。",
  "skills.savedTab": "已儲存",
  "skills.mySkills": "我的技能",
  "skills.sharedTab": "組織共享",
  "skills.saved": "已儲存",
  "skills.download": "下載",
  "skills.downloadSuccess": "技能已下載到這台電腦。",
  "skills.downloadError": "下載失敗",
  "skills.noSharedSkills": "目前沒有共享技能",
  "skills.noSharedSkillsDesc": "尚未有人與你的組織分享技能。",
  "skills.sharedLoadError": "無法載入組織的技能。",
  "skills.noSharedForVersion": "{version} 目前沒有共享的技能。",
  "skills.deleteSuccess": "技能已刪除",
  "skills.deleteError": "刪除失敗",
  "skills.deleteCloud": "從組織移除",
  "skills.publishNotOwner": "此技能由其他人發布，只有發布者可以發布新版本。",
  "skills.deleteCloudTitle": "要從組織移除這個技能嗎？",
  "skills.deleteCloudDesc": "成員將不再看到或下載它。已下載的副本會留在各自的電腦上；你的副本會顯示為未發布。",
  "toolEditor.details": "詳細資料",
  "skills.published": "已發布",
  "skills.newFolder": "新增資料夾",
  "skills.newFolderDesc": "在這台電腦為 {version} 建立技能資料夾。",
  "skills.folderNamePlaceholder": "資料夾名稱",
  "skills.createFolder": "建立",
  "skills.folderCreateSuccess": "資料夾已建立",
  "skills.folderCreateError": "無法建立資料夾",
  "skills.moveToTargetFolder": "移至 {target} 資料夾",
  "skills.rootFolder": "根目錄",
  "skills.moveSuccess": "技能已移動",
  "skills.moveError": "無法移動技能",
  "skills.folderEmpty": "此資料夾目前沒有技能。",
  "skills.skillCount": "{count} 個技能",
  "skills.unknownVersion": "版本不明",
  "skills.connected": "已連線此版本的軟體",
  "skills.run.noPermission": "您沒有執行技能的權限（需要 skill:run）。",
  "skills.run.disabled": "此技能已停用，請先啟用再執行。",
  "skills.run.unknownVersion": "此技能未註明適用版本，無法對應到任何軟體。",
  "skills.run.noInstance": "請在標題列選擇已連線的 {target} 軟體以執行此技能。",
  "skills.run.wrongInstance": "此技能適用於 {target}，目前所選軟體為 {selected}。請選擇 {target} 軟體以執行。",
  "skills.deleteSkillTitle": "刪除技能",
  "skills.deleteSkillDesc": "將從這台電腦移除此技能及其執行紀錄，且無法復原。",
  "skills.deleteAllVersions": "將刪除此技能的全部 {count} 個版本：{versions}。",
  "skills.deleteOnlyVersion": "將刪除此技能唯一的版本：{versions}。",
  "skills.category.mep": "機電",
  "skills.category.structure": "結構",
  "skills.category.revitOps": "Revit 操作",
  "skills.category.default": "預設",
  "toolEditor.run": "執行",
  "toolEditor.running": "執行中...",
  "toolEditor.parameters": "參數",
  "toolEditor.paramDefault": "預設值",
  "toolEditor.noParameters": "此技能不需要參數。",
  "toolEditor.selectValue": "選擇數值",
  "toolEditor.result": "結果",
  "toolEditor.summary": "摘要",
  "toolEditor.resultDetails": "結果詳情",
  "toolEditor.dataTables": "資料表",
  "toolEditor.warnings": "警告",
  "toolEditor.delete": "刪除",
  "toolEditor.code": "程式碼",
  "toolEditor.executedAt": "上次執行時間",
  "toolEditor.lines": "行",
  "toolEditor.characters": "字元",
  "toolEditor.enabled": "啟用技能",
  "toolEditor.enableSuccess": "已啟用技能",
  "toolEditor.disableSuccess": "已停用技能",
  "toolEditor.toggleEnabledError": "更新啟用狀態失敗",
  "skillEdit.versionsTitle": "版本紀錄",
  "skillEdit.chatTitle": "AI 編輯",
  "skillEdit.emptyState": "描述你想要的修改 — 代理會修訂這個技能的程式碼，儲存前先詢問你，每次儲存都會標記為新版本。",
  "skillEdit.inputPlaceholder": "描述要修改這個技能的哪裡…",
  "skillEdit.awaitingApprovalPlaceholder": "等待你在核准卡上做出決定…",
  "skillEdit.viewCode": "檢視程式碼",
  "skillEdit.backToChat": "返回對話",
  "skillEdit.currentThread": "目前",
  "skillEdit.titleCommit": "儲存新技能版本",
  "skills.edit.noPermission": "你的角色沒有編輯技能的權限。",
  "skills.edit.runInProgress": "此技能正在執行，模型較大時可能需要幾分鐘；執行完成後即可繼續編輯。",
  "skills.edit.unknownVersion": "這個技能沒有標明目標版本，因此無法編輯。",
  "skills.edit.noInstance": "編輯需要連線 {target} 來測試修改。請選擇一個已連線的 {target} 軟體。",
  "skills.edit.wrongInstance": "這個技能的目標是 {target}；目前選擇的軟體是 {selected}。請選擇相符的軟體才能編輯。",
  "skills.targetMismatch": "此技能適用於 {target}，目前所選軟體為 {selected}。請選擇 {target} 軟體以執行或編輯。",
  "skills.targetMismatchChip": "適用於 {target}",
  "skills.edit.mismatchShort": "目前所選軟體不適用。",
  "skillRegenerate.successTitle": "技能已更新",
  "skillRegenerate.successDescription": "已儲存為 v{version}。",
  "skillRegenerate.testHint": "執行一次以確認修改。",
  "skillVersions.switchBlockedHint": "技能執行中、代理工作中或有待決定事項時，暫停切換版本。",
  "skillVersions.switchedTitle": "版本已切換",
  "skillVersions.switchedDescription": "工作副本現在是 v{version}。",
  "skillVersions.switchError": "無法切換版本。",
  "toolEditor.edit": "編輯",
  "toolEditor.update": "更新",
  "toolEditor.unsaved": "未儲存",
  "toolEditor.saving": "儲存中…",
  "toolEditor.done": "完成",
  "toolEditor.updateSuccess": "技能已更新。",
  "toolEditor.updateError": "無法更新技能。",
  "toolEditor.namePlaceholder": "技能名稱",
  "toolEditor.description": "描述",
  "toolEditor.descriptionPlaceholder": "這個技能的用途…",
  "toolEditor.noDescription": "尚無描述。",
  "toolEditor.publish": "發布",
  "toolEditor.publishing": "發布中…",
  "toolEditor.publishSuccess": "技能已發布到你的組織。",
  "toolEditor.publishError": "發布失敗",
  "toolEditor.confirmPublishTitle": "要發布這個技能嗎？",
  "toolEditor.confirmPublishDesc": "目前版本會分享到組織的雲端，成員可以下載並執行。",
  "toolEditor.lockedDuringUpdate": "代理正在更新這個技能，暫時鎖定。",
  "common.newThread": "新對話",
  // The sidebar rail. These were English literals in `AppShell` and did not
  // translate with the rest of the pane; `routes.ts` now holds keys, not words.
  "shell.nav.new": "新對話",
  "shell.nav.chat": "對話",
  // 側邊欄清單最上方的目前專案：標籤在上，專案名稱在下。
  "shell.project.label": "專案",
  "shell.project.none": "選擇專案",
  // 側邊欄的對話區：執行中的在最上方，接著是此專案最新的幾則。
  "shell.chats.title": "對話",
  "shell.chats.viewAll": "查看全部（{count}）",
  // 「查看全部」開啟的對話頁：此專案的所有對話。
  "chats.title": "對話",
  "chats.count": "{count} 則對話",
  "chats.searchPlaceholder": "依標題搜尋對話",
  "chats.noMatches": "沒有符合搜尋的對話",
  "chats.group.today": "今天",
  "chats.group.yesterday": "昨天",
  "chats.group.week": "過去 7 天",
  "chats.group.earlier": "更早",
  // 側邊欄：仍在執行或等待確認的對話（#193）。
  "shell.running.untitled": "新對話",
  "shell.running.working": "處理中",
  "shell.running.needsApproval": "等待確認",
  "shell.running.editing": "編輯中：{name}",
  "shell.running.untitledSkill": "技能",
  "shell.running.skillEditor": "技能編輯",
  "shell.account.notSignedIn": "尚未登入",
  "shell.account.noOrganization": "尚未加入組織",
  "shell.sidebarCollapse": "收合側邊欄",
  "shell.sidebarExpand": "展開側邊欄",
  "common.historyLoading": "載入對話中…",
  "toast.regenThreadNotYours": "那段對話屬於這台電腦上的另一個帳號，因此保持私密。請開始新對話。",
  "toast.regenThreadVerifyFailed": "目前無法驗證那段對話 — 請再試一次。",
  "skillVersions.published": "已發布",
  "skillVersions.current": "目前版本",
  "skillVersions.empty": "尚未儲存任何版本。",
  "skillVersions.loadError": "載入版本清單失敗。",
  "skillVersions.viewing": "正在檢視 v{version}（唯讀）",
  "skillVersions.viewingWarning": "正在檢視 v{version} — 執行與編輯將套用於目前版本",
  "skillVersions.backToCurrent": "回到目前版本",
  "skillHistory.title": "執行紀錄",
  "skillHistory.empty": "尚無執行紀錄",
  "skillHistory.emptyDesc": "手動執行此技能後，紀錄會顯示在這裡。",
  "skillHistory.loadError": "載入執行紀錄失敗。",
  "skillHistory.detailLoadError": "載入執行詳細資料失敗。",
  "skillHistory.success": "成功",
  "skillHistory.failed": "失敗",
  "skillHistory.timedOut": "逾時",
  "skillHistory.executedAt": "執行時間",
  "skillHistory.duration": "耗時",
  "skillHistory.revitVersion": "Revit 版本",
  "skillHistory.skillVersion": "技能版本",
  "skillHistory.parameters": "執行參數",
  "skillHistory.viewResult": "檢視完整結果",
  "skillHistory.resultTitle": "執行結果",
  "toast.skillLoadError": "載入技能時發生錯誤",
  "toast.skillRunSuccess": "技能「{name}」執行成功",
  "toast.skillRunError": "執行技能時發生錯誤：{error}",
  "toast.skillDeleteSuccess": "技能已刪除",
  "toast.skillDeleteError": "刪除技能時發生錯誤",
  "toast.missingParams": "缺少必要參數",
  "toast.missingParamsDesc": "請填寫以下必要參數：{params}",
  "common.cancel": "取消",
  "common.loading": "載入中...",
  "common.back": "返回",
  // Revit Family Builder（#255），沿用 bimgo-ai-web 的 familyAuthoring.* 文字。
  "familyBuilder.nav": "元件",
  "familyBuilder.title": "Revit Family Builder",
  "familyBuilder.chatTitle": "與 AI 對話編輯",
  "familyBuilder.details": "元件資訊",
  "familyBuilder.parameters": "參數",
  "familyBuilder.summary": "執行摘要",
  "familyBuilder.summaryEmpty": "完成第一次建立或修改後，Agent 的執行摘要會顯示在這裡。",
  "familyBuilder.emptyTitle": "描述你想建立或修改的 Revit 元件",
  "familyBuilder.emptyDescription": "可以直接描述需求，或附上圖片、三視圖與產品型錄作為參考。",
  "familyBuilder.inputPlaceholder": "描述你想建立或修改的內容…",
  "familyBuilder.newComponent": "新增元件",
  "familyBuilder.newThread": "新增對話",
  "familyBuilder.noDescription": "尚無說明。",
  "familyBuilder.listEmptyTitle": "尚未建立 Revit 元件",
  "familyBuilder.listEmptyDescription": "建立第一個元件後，可以在這裡持續修改、調整參數與查看版本。",
  "familyBuilder.applyParameters": "套用參數調整",
  "familyBuilder.parametersApplied": "參數已套用至目前的 Revit 元件。",
  "familyBuilder.fixExecutionError": "請 Agent 修復",
  "familyBuilder.fixExecutionErrorPrompt": "請幫我修正套用參數時發生的錯誤：",
  "familyBuilder.fixParameterContractPrompt": "請修正元件的參數介面契約：",
  "familyBuilder.parameterContractInvalid": "參數設定不完整，無法顯示調整介面。",
  "familyBuilder.uncommittedChanges": "目前有尚未完成的修改，尚未包含在最新成功版本中。",
  "familyBuilder.continueUncommittedChanges": "請 Agent 繼續處理",
  "familyBuilder.uncommittedChangesPrompt": "請繼續處理目前尚未完成、尚未包含在最新成功版本中的元件修改。請檢查現有 code.cs 與 SKILL.md，完成必要修正後重新 Build 並驗證結果。",
  "familyBuilder.versioningPartialSuccess": "元件已更新，但版本未儲存",
  "familyBuilder.versionSaveRepairPrompt": "元件已更新，但版本未儲存。請檢查目前的 code.cs、metadata.json 與 SKILL.md，完成必要修正後重新 Build，確認版本已儲存。",
  "familyBuilder.viewRefreshFailed": "元件已更新，但畫面同步失敗。請重新開啟此元件以取得最新內容。",
  "familyBuilder.activeRfaRequired": "請在 Revit 切回此元件原本的 RFA，再送出新的請求。本次操作未執行。",
  "familyBuilder.reopenRfaRequired": "原元件文件已關閉或無法使用。請在此重新開啟，再送出新的請求。",
  "familyBuilder.reopenRfa": "重新開啟原元件",
  "familyBuilder.attach": "加入參考資料",
  "familyBuilder.send": "送出",
  "familyBuilder.stop": "停止",
  "familyBuilder.stopped": "已停止執行。",
  "familyBuilder.evidence": "3D 驗證截圖",
  "familyBuilder.missingSession": "登入狀態已失效，請重新登入。",
  "familyBuilder.rfaActivating": "正在開啟並切換到此元件的 RFA…",
  "familyBuilder.rfaBindingRequired": "此元件尚未連結 RFA。請選擇它的既有 RFA 檔案。",
  "familyBuilder.rfaNotFound": "找不到此元件的 RFA。請選擇目前正確的檔案位置。",
  "familyBuilder.rfaConflict": "記錄的 RFA 屬於另一個 BimGo AI 元件，已停止開啟與 Build。",
  "familyBuilder.rfaUnsaved": "此元件的 RFA 尚未儲存。",
  "familyBuilder.rfaSaveFailed": "無法儲存目前的 RFA。請確認檔案不是唯讀且具有寫入權限。",
  "familyBuilder.rfaActivationFailed": "尚未確認此元件的 RFA 已就緒。請查看下方原因。",
  "familyBuilder.rfaBusy": "請先完成 Revit 目前的操作或關閉對話框，再重試開啟 RFA。",
  "familyBuilder.rfaOpenCancelled": "已取消開啟 RFA。",
  "familyBuilder.rfaRequestExpired": "開檔請求已逾期。請確認 Revit 目前狀態，再手動重試。",
  "familyBuilder.rfaFocusPending": "RFA 已開啟，已請求切換至 3D 視圖。",
  "familyBuilder.rfaFocusFailed": "RFA 已開啟，但未完成 3D 視圖切換。您仍可繼續操作元件。",
  "familyBuilder.rfaNotReady": "請先在 Revit 開啟此元件的 RFA，再繼續操作。",
  "familyBuilder.instanceBusy": "此 Revit 正在執行其他工作（對話或技能），請待其完成後再重試。",
  "familyBuilder.openRfaInRevit": "在 Revit 開啟",
  "familyBuilder.locateRfa": "選擇 RFA",
  "familyBuilder.restoreNote": "還原版本只會還原程式碼，不會重新建立 RFA；請再次 Build 以更新 Revit 中的元件。",
  "familyBuilder.restored": "已還原至 v{version}。請再次 Build 以更新 Revit 中的元件。",
  "familyBuilder.noRevit": "請先開啟 Revit 才能使用元件",
  "familyBuilder.noRevitDesc": "在標題列選取 Revit，以顯示該版本儲存的元件。",
  "familyBuilder.noRevitHint": "在標題列選取要使用的 Revit。",
  "familyBuilder.notRevit": "建立元件僅支援 Revit",
  "familyBuilder.unsupported": "目前的本機 Agent 版本不支援建立元件，請更新桌面應用程式。",
  "familyBuilder.activity.preparing": "正在準備元件建立工作",
  "familyBuilder.activity.reviewingArtifacts": "檢查目前元件資料",
  "familyBuilder.activity.updatingArtifacts": "更新元件規格與執行摘要",
  "familyBuilder.activity.editingCode": "編寫並檢查元件程式",
  "familyBuilder.activity.inspecting": "檢查 Revit 元件",
  "familyBuilder.activity.building": "建立並視覺驗證元件",
  "familyBuilder.activity.preparingSummary": "Agent 正在處理",
  "familyBuilder.activity.generic": "執行元件建立步驟",
};

export const STRINGS: Record<Locale, Dict> = { en, zh };
